import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Sparkles, ScanFace, Clapperboard, SlidersHorizontal, Construction,
  ChevronLeft, Type as TypeIcon, ImagePlus, Plus, Layers, Cpu, Dice5, Maximize2,
  Brush, Expand, History, Sun, MoveUpRight, Gauge, ZoomIn,
  MoveHorizontal, MoveVertical, Orbit, Snowflake, Repeat, Clock,
  Film, Image as ImageIcon, Camera, Wrench, ChevronDown, VolumeX, Wind,
  Building2, Wand2, X, Loader2, Zap, Eraser, Mic, Video, KeyRound, Clock3,
  Save, FolderOpen, Trash2, Ban, Music2, Download, Upload, Library,
  Gamepad2, Rocket, Swords, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, User, Mountain, ShieldAlert,
  MapPin, RotateCcw,
} from "lucide-react";
import { mistApi, pipelinesApi, streamAsset, fetchAssetFile, downloadAsset, pipelineErr, guardToast } from "@/api/mist";
import SaveDestinations from "@/components/gallery/SaveDestinations";
import { previewUrl } from "@/lib/imageCompress";
import { GenerateBar, ResultCard, ExportHandlers, CoinBalanceChip } from "@/components/mist/shared";
import KeyModePill from "@/components/mist/KeyModePill";
import { MicDictate } from "@/components/mist/MicDictate";
import TextOverlayStudio, { VaultPickerModal } from "@/components/mist/TextOverlayStudio";
import VaultPickButton from "@/components/mist/VaultPickButton";
import { useStudioParam } from "@/hooks/useStudioUrl";
import { consumeDirectorPrompt, getDirectorContext, setPlaceResult } from "@/lib/directorPrefill";
import { ProjectVaultFlow } from "@/components/mist/ProjectVault";
import GameDemoRecorder from "@/components/mist/GameDemoRecorder";
import TextToImageWizard from "@/components/mist/TextToImageWizard";

const MAX_CHARS = 2000;
const STYLES = ["Anime", "3D Render", "Oil Painting", "Cinematic", "Cyberpunk"];
const RATIOS = [{ k: "1:1", p: "Album/Post" }, { k: "9:16", p: "Reels/Story" }, { k: "16:9", p: "Wide" }];
const MOODS = ["Golden Hour", "Studio Lighting", "Neon"];
const MODELS = ["Nano Banana", "Stable Diffusion XL", "Flux", "Midjourney"];

/* -------------------------- Mist Create landing -------------------------- */
const OPTIONS = [
  { key: "generate", title: "Generate", sub: "Create image from text · Face Swap", Icon: Sparkles, color: "#A4DCFA" },
  { key: "director", title: "Director Mode", sub: "Storyboard → video · 100% BYOK", Icon: Film, color: "#FFE08A" },
  { key: "animate", title: "Animate", sub: "Image → motion", Icon: Clapperboard, color: "#FFC9A3" },
  { key: "effects", title: "Effects", sub: "Text · stickers · filters · frames", Icon: SlidersHorizontal, color: "#E8D3FF" },
  { key: "strip", title: "Overlay Removal", sub: "Erase UI overlays → clean plate", Icon: Eraser, color: "#9EFFB8" },
];

/* --------------------------- Generate sub-flow --------------------------- */
const GEN_TABS = [
  { key: "text", label: "Text to Image", Icon: TypeIcon },
  { key: "place", label: "Place", Icon: MapPin },
  { key: "faceswap", label: "Face Swap", Icon: ScanFace },
];

/* Dispatch the render-busy signal so the founder PiP/test-feed banner auto-tucks
   while a generation/swap is in flight (prevents it masking the bottom buttons). */
const setRenderBusy = (active) =>
  window.dispatchEvent(new CustomEvent("mist:director-render", { detail: { active } }));

function GenerateFlow() {
  // Land directly on Place when the Director routed here for a scene plate.
  const [tab, setTab] = useState(() => (getDirectorContext() ? "place" : "text"));
  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-white" data-testid="create-generate">
      <div className="shrink-0 grid grid-cols-3 gap-2 px-4 pt-3 pb-3 bg-white border-b-2 border-black" data-testid="create-generate-tabs">
        {GEN_TABS.map((t) => {
          const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`create-tab-${t.key}`}
              className={`flex items-center justify-center gap-1.5 border-2 border-black rounded-none px-1.5 py-3 font-mono text-[9px] font-black uppercase tracking-[0.06em] shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform ${on ? "bg-[#FFDE4D] text-black" : "bg-white text-black/50"}`}>
              <t.Icon size={13} strokeWidth={2.4} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "text" ? <TextToImageWizard /> : tab === "place" ? <PlaceFlow /> : <FaceSwapFlow />}
    </div>
  );
}

/* Light neo-brutalist result card — white canvas, black frame, hard shadow.
   `compareSrc` (Face Swap) enables a tap-to-peek Before/After against the original. */
function ImageResult({ result, compareSrc }) {
  const [src, setSrc] = useState(null);
  const [showBefore, setShowBefore] = useState(false);
  useEffect(() => {
    let url;
    if (result?.asset?.id) streamAsset(result.asset.id).then((u) => { url = u; setSrc(u); }).catch(() => {});
    setShowBefore(false);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [result?.asset?.id]);
  if (!result) return null;
  const canCompare = !!compareSrc && !!src;
  return (
    <div className="border-2 border-black bg-white p-2 shadow-[4px_4px_0px_#000]" data-testid="mist-result">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[8px] font-black uppercase tracking-widest bg-black text-[#FFDE4D] px-2 py-0.5">{result.engine || "done"}</span>
        {canCompare && (
          <span className="font-mono text-[8px] font-black uppercase tracking-widest bg-[#6BCB77] text-black px-2 py-0.5 border border-black" data-testid="mist-result-compare-badge">
            {showBefore ? "Before" : "After"}
          </span>
        )}
      </div>
      {src
        ? (
          <button
            type="button"
            onClick={() => canCompare && setShowBefore((v) => !v)}
            data-testid="mist-result-compare"
            className={`block w-full ${canCompare ? "cursor-pointer" : "cursor-default"}`}
            title={canCompare ? "Tap to compare Before / After" : undefined}>
            <img src={showBefore && canCompare ? compareSrc : src} alt="generated"
              className="w-full border-2 border-black" data-testid="mist-result-image" />
          </button>
        )
        : <div className="h-48 grid place-items-center border-2 border-dashed border-black"><Loader2 size={18} className="text-black animate-spin" /></div>}
      {canCompare && (
        <p className="mt-1 text-center font-mono text-[8px] uppercase tracking-widest text-black/50" data-testid="mist-result-compare-hint">Tap image → {showBefore ? "see swap" : "see original"}</p>
      )}
      {result.asset?.id && (
        <SaveDestinations assetId={result.asset.id} track="IMAGES" filename="mist_image" ext="png" commit className="mt-2" size="sm" />
      )}
    </div>
  );
}

/* One frame chip in the Continuity Filmstrip — streams its thumbnail. */
function FilmstripThumb({ frame, index, active, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (frame?.id) streamAsset(frame.id).then((u) => { url = u; setSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [frame?.id]);
  return (
    <button onClick={onClick} data-testid={`filmstrip-frame-${index}`}
      className={`relative shrink-0 w-16 h-16 border-2 bg-white overflow-hidden ${active ? "border-[#6BCB77] shadow-[3px_3px_0px_#6BCB77]" : "border-black shadow-[3px_3px_0px_#000]"} active:translate-x-[1px] active:translate-y-[1px] active:shadow-none`}>
      {src ? <img src={src} alt={`scene ${index + 1}`} className="w-full h-full object-cover" />
           : <div className="w-full h-full grid place-items-center"><Loader2 size={12} className="text-black animate-spin" /></div>}
      <span className="absolute bottom-0 left-0 bg-black text-[#FFDE4D] font-mono text-[7px] font-black px-1">{index + 1}</span>
    </button>
  );
}

/* ---------------- TAB A — 2-Click Text-to-Image + Continuity ------------- */
const ADHERENCE = [
  { k: "loose", label: "Loose" },
  { k: "balanced", label: "Balanced" },
  { k: "strict", label: "Strict" },
];

// Popular Text-to-Image style presets (compiler applies the look server-side).
const T2I_STYLES = [
  { k: "hyper_realistic", label: "Hyper-Realistic Studio Photography" },
  { k: "street_photo", label: "Gritty Realistic Street Photography" },
  { k: "pixar_3d", label: "3D Pixar / Disney Game Graphic" },
  { k: "cyber_gothic", label: "Cyber-Gothic / Y2K Futuristic" },
  { k: "synthwave", label: "Synthwave / Neon Grid" },
  { k: "dreamcore", label: "Dreamcore / Neo-Surrealism" },
  { k: "acid_grunge", label: "Acid Graphics / Neo-Grunge Rave" },
  { k: "psychedelic", label: "Psychedelic / Fluid Abstract" },
];

function TextToImageFlow() {
  const [prompt, setPrompt] = useState(() => consumeDirectorPrompt());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [seedId, setSeedId] = useState(null);    // locked identity seed (asset_id)
  const [seedSrc, setSeedSrc] = useState(null);   // preview of the locked seed
  const [armed, setArmed] = useState(false);      // continuity mode active
  const [story, setStory] = useState([]);         // filmstrip: [{id, track}]
  const [adherence, setAdherence] = useState("balanced");
  const [style, setStyle] = useState(null);       // t2i style preset
  const [aspect, setAspect] = useState("9:16");    // output aspect ratio (1:1 = album cover)
  const [refs, setRefs] = useState([]);            // multi-image references [{id, src}]
  const refUploadRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const uploadRef = useRef(null);

  const onPrompt = (e) => setPrompt(e.target.value.slice(0, MAX_CHARS));

  const generateBase = async () => {
    const text = prompt.trim();
    if (!text) { toast.error("Describe a scene to generate"); return; }
    setBusy(true); setResult(null); setArmed(false); setSeedId(null); setSeedSrc(null);
    setRenderBusy(true);
    try {
      const { job_id } = await pipelinesApi.generateImage(text, style, refs.map((r) => r.id), aspect);
      const j = await pipelinesApi.poll(job_id);
      if (j.status === "completed" && j.asset_id) {
        const asset = { id: j.asset_id, track: j.track || "image" };
        setResult({ asset, engine: j.engine });
        setStory([asset]);          // start a fresh story
        guardToast(j);
        if (j.engine && j.engine.startsWith("fallback")) toast("Rendered via fallback engine", { icon: "🧪" });
      } else { toast.error(j.error || "Image generation failed"); }
    } catch (e) { toast.error(pipelineErr(e, "Generation failed")); }
    finally { setBusy(false); setRenderBusy(false); }
  };

  // Green button — arms continuity on first tap, generates the next scene after.
  const onNextScene = async () => {
    if (!armed) {
      if (!result?.asset?.id) return;
      setSeedId(result.asset.id);
      try { const u = await streamAsset(result.asset.id); setSeedSrc(u); } catch {}
      setArmed(true);
      setPrompt("");
      toast.success("Identity locked ✓ — describe your next scene");
      return;
    }
    const text = prompt.trim();
    if (!text) { toast.error("Describe the next scene first"); return; }
    setBusy(true); setResult(null);
    setRenderBusy(true);
    try {
      const { job_id } = await pipelinesApi.nextScene(seedId, text, adherence, style);
      const j = await pipelinesApi.poll(job_id);
      if (j.status === "completed" && j.asset_id) {
        const asset = { id: j.asset_id, track: j.track || "image" };
        setResult({ asset, engine: j.engine });
        setSeedId(j.asset_id);                 // chain the new frame as the next seed
        try { const u = await streamAsset(j.asset_id); setSeedSrc(u); } catch {}
        setStory((s) => [...s, asset]);        // append to the filmstrip
        setPrompt("");
        guardToast(j);
      } else { toast.error(j.error || "Next scene failed"); }
    } catch (e) { toast.error(pipelineErr(e, "Next scene failed")); }
    finally { setBusy(false); setRenderBusy(false); }
  };

  // Filmstrip tap — revisit + branch continuity from any earlier frame.
  const branchFrom = async (frame) => {
    if (busy) return;
    setResult({ asset: frame, engine: "revisited" });
    setSeedId(frame.id);
    try { const u = await streamAsset(frame.id); setSeedSrc(u); } catch {}
    setArmed(true);
    setPrompt("");
    toast.success("Branching from this scene — describe the next one");
  };

  // Seed the Next-Scene flow from an existing asset (uploaded or vault-picked):
  // it becomes the current result + locked seed, so continuity works identically.
  const seedFromAsset = async (asset, note = "Image locked ✓ — describe your next scene") => {
    setResult({ asset, engine: "uploaded" });
    setStory([asset]);
    setSeedId(asset.id);
    setArmed(true);           // arm immediately so the seed-lock chip renders without lag
    setPrompt("");
    toast.success(note);
    try { const u = await streamAsset(asset.id); setSeedSrc(u); } catch {}
  };

  const onUploadFile = async (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setBusy(true); setRenderBusy(true);
    // Optimistic preview — show the picked image the instant it's chosen, while
    // it compresses + uploads in the background.
    const localUrl = previewUrl(f);
    if (localUrl) { setSeedSrc(localUrl); setArmed(true); }
    try {
      const r = await pipelinesApi.uploadSeed(f);
      if (r.asset_id) await seedFromAsset({ id: r.asset_id, track: "image" });
      else toast.error("Upload failed");
    } catch (err) { toast.error(pipelineErr(err, "Upload failed")); }
    finally { setBusy(false); setRenderBusy(false); if (localUrl) URL.revokeObjectURL(localUrl); }
  };

  const onPickFromVault = async (item) => {
    setPickerOpen(false);
    if (item?.id) await seedFromAsset({ id: item.id, track: "image" }, "Vault image locked ✓ — describe your next scene");
  };

  // Multi-image references — up to 4 images that guide the generation (subject,
  // outfit, environment, palette). Each uploads to the vault and returns an id.
  const onAddRef = async (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    if (refs.length >= 4) { toast.error("Up to 4 reference images"); return; }
    const localUrl = previewUrl(f);
    try {
      const r = await pipelinesApi.uploadSeed(f);
      if (r.asset_id) setRefs((prev) => [...prev, { id: r.asset_id, src: localUrl }]);
      else { toast.error("Reference upload failed"); if (localUrl) URL.revokeObjectURL(localUrl); }
    } catch (err) { toast.error(pipelineErr(err, "Reference upload failed")); if (localUrl) URL.revokeObjectURL(localUrl); }
  };
  const removeRef = (id) => setRefs((prev) => prev.filter((r) => r.id !== id));

  return (
    <div className="h-full w-full flex flex-col min-h-0 bg-white" data-testid="create-text">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-3 space-y-4">
        {armed && seedSrc && (
          <div className="flex items-center gap-3 border-2 border-black bg-[#6BCB77] p-2 shadow-[4px_4px_0px_#000]" data-testid="create-seed-lock">
            <img src={seedSrc} alt="locked identity" className="w-16 h-16 object-cover border-2 border-black" />
            <div>
              <p className="font-mono text-[10px] font-black uppercase tracking-widest text-black">Identity Seed Locked ✓</p>
              <p className="font-mono text-[9px] text-black/70">Same person · new location</p>
            </div>
          </div>
        )}

        {/* Continuity Filmstrip — every scene in the current story; tap to branch. */}
        {story.length > 0 && (
          <div data-testid="continuity-filmstrip">
            <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">Story filmstrip · tap a frame to branch</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {story.map((f, i) => (
                <FilmstripThumb key={f.id} frame={f} index={i}
                  active={result?.asset?.id === f.id}
                  onClick={() => branchFrom(f)} />
              ))}
            </div>
          </div>
        )}

        <label className="block">
          <span className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-black/60">
            {armed ? "Next scene" : "Describe your scene"}
          </span>
          <textarea value={prompt} onChange={onPrompt} rows={5} maxLength={MAX_CHARS} data-testid="create-prompt"
            placeholder="Describe any scene you want (e.g., 'Two lovers walking hand in hand down the beach at sunset')..."
            className="mt-1 w-full bg-white text-black placeholder:text-black/40 border-2 border-black rounded-none p-3 font-mono text-xs outline-none focus:ring-2 focus:ring-[#FFDE4D] shadow-[4px_4px_0px_#000] resize-none break-words" />
          <span data-testid="create-counter" className={`mt-1 block text-right font-mono text-[9px] uppercase tracking-widest ${prompt.length >= MAX_CHARS ? "text-rust" : "text-black/40"}`}>{prompt.length} / {MAX_CHARS}</span>
        </label>

        {/* Style presets — anime / realistic / cartoon / sci-fi / portrait / … */}
        <div data-testid="style-picker">
          <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">Style</p>
          <div className="flex flex-wrap gap-2">
            {T2I_STYLES.map((s) => (
              <button key={s.k} onClick={() => setStyle(style === s.k ? null : s.k)} data-testid={`style-${s.k}`}
                className={`border-2 border-black px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${style === s.k ? "bg-[#FFDE4D] text-black" : "bg-white text-black/50"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Aspect ratio — 1:1 for album covers, 9:16 reels, 16:9 wide */}
        <div data-testid="ratio-picker">
          <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">Aspect Ratio</p>
          <div className="grid grid-cols-3 gap-2">
            {RATIOS.map((r) => (
              <button key={r.k} onClick={() => setAspect(r.k)} data-testid={`ratio-${r.k.replace(":", "-")}`}
                className={`flex flex-col items-center gap-0.5 border-2 border-black py-2 shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${aspect === r.k ? "bg-[#FFDE4D] text-black" : "bg-white text-black/50"}`}>
                <span className="font-mono text-[11px] font-black">{r.k}</span>
                <span className="font-mono text-[7px] font-black uppercase tracking-widest">{r.p}</span>
              </button>
            ))}
          </div>
        </div>
        {!armed && (
          <div data-testid="ref-images">
            <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">Reference images · optional (up to 4)</p>
            <input ref={refUploadRef} type="file" accept="image/*" hidden onChange={onAddRef} data-testid="ref-image-input" />
            <div className="flex flex-wrap gap-2">
              {refs.map((r) => (
                <div key={r.id} className="relative w-16 h-16 border-2 border-black shadow-[3px_3px_0px_#000]" data-testid={`ref-thumb-${r.id}`}>
                  <img src={r.src} alt="reference" className="w-full h-full object-cover" />
                  <button onClick={() => removeRef(r.id)} data-testid={`ref-remove-${r.id}`}
                    className="absolute -top-2 -right-2 w-5 h-5 grid place-items-center bg-rust text-white border-2 border-black font-mono text-[10px] font-black">×</button>
                </div>
              ))}
              {refs.length < 4 && (
                <button onClick={() => refUploadRef.current?.click()} disabled={busy} data-testid="ref-add-btn"
                  className="w-16 h-16 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-black bg-white shadow-[3px_3px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
                  <Plus size={16} /><span className="font-mono text-[7px] font-black uppercase">Add ref</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Upload / vault-pick a starting image → same Next-Scene continuity flow. */}
        {!armed && (
          <div data-testid="upload-seed">
            <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">Or start from your own image</p>
            <input ref={uploadRef} type="file" accept="image/*" hidden onChange={onUploadFile} data-testid="upload-seed-input" />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => uploadRef.current?.click()} disabled={busy} data-testid="upload-seed-device"
                className="flex items-center justify-center gap-2 border-2 border-black bg-white py-2.5 font-mono text-[9px] font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
                <Upload size={13} /> Upload image
              </button>
              <button onClick={() => setPickerOpen(true)} disabled={busy} data-testid="upload-seed-vault"
                className="flex items-center justify-center gap-2 border-2 border-black bg-white py-2.5 font-mono text-[9px] font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
                <Library size={13} /> From library
              </button>
            </div>
          </div>
        )}

        {/* Adherence dial — only relevant once continuity is armed. */}
        {armed && (
          <div data-testid="adherence-dial">
            <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">Adherence · how much the location changes</p>
            <div className="grid grid-cols-3 gap-2">
              {ADHERENCE.map((a) => (
                <button key={a.k} onClick={() => setAdherence(a.k)} data-testid={`adherence-${a.k}`}
                  className={`border-2 border-black py-2 font-mono text-[9px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${adherence === a.k ? "bg-[#FFDE4D] text-black" : "bg-white text-black/50"}`}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <ImageResult result={result} />
        {result?.asset?.id && (
          <>
            <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-black/50" data-testid="storyboard-savehint">Not saved yet — choose where to keep this frame:</p>
            <ExportHandlers assetId={result.asset.id} track="image" filename="mist_scene" />
          </>
        )}
      </div>

      <div className="shrink-0 border-t-2 border-black bg-white px-4 py-3 space-y-2" data-testid="create-text-bar">
        <button onClick={generateBase} disabled={busy || !prompt.trim()} data-testid="create-generate-base"
          className="w-full flex items-center justify-center gap-2 bg-[#FFDE4D] text-black font-black font-mono text-xs uppercase tracking-widest py-4 border-2 border-black rounded-none shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
          {busy && !armed ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} ➔ Generate Base Image
        </button>
        {result && (
          <button onClick={onNextScene} disabled={busy} data-testid="create-generate-next"
            className="w-full flex items-center justify-center gap-2 bg-[#6BCB77] text-black font-black font-mono text-xs uppercase tracking-widest py-4 border-2 border-black rounded-none shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
            {busy && armed ? <Loader2 size={16} className="animate-spin" /> : <MoveUpRight size={16} />} ➔ Generate Next Scene
          </button>
        )}
      </div>
      {pickerOpen && <VaultPickerModal onClose={() => setPickerOpen(false)} onPicked={onPickFromVault} bucket="images" title="Pick a starting image" />}
    </div>
  );
}

/* ---------------- TAB B — Lazy-Man Face Swap (dual drop box) ------------- */
/* ---------- TAB — PLACE objects/characters into a target scene ---------- */
const PLACE_MAX = 5;

/* ==========================================================================
   PLACE ⇆ DIRECTOR bridge header — shown ONLY when the Director routed the user
   here to design a scene plate. Renders the incoming scene context + a
   "Save & Return" action that hands the winning asset id back to the Director.
   Non-breaking: renders nothing when there is no active Director context.
   ========================================================================== */
function PlaceWorkspaceHeader({ resultAssetId }) {
  const [context] = useState(() => getDirectorContext());
  const [thumbs, setThumbs] = useState({});

  useEffect(() => {
    let alive = true;
    (context?.characters || []).forEach(async (c) => {
      if (!c.thumb_asset_id) return;
      try { const u = await streamAsset(c.thumb_asset_id); if (alive) setThumbs((t) => ({ ...t, [c.character_id]: u })); }
      catch { /* thumb optional */ }
    });
    return () => { alive = false; };
  }, [context]);

  if (!context) return null;

  const saveAndReturn = () => {
    if (!resultAssetId) return;
    setPlaceResult(resultAssetId, context);
    const p = new URLSearchParams(window.location.search);
    p.set("studio", "directormode"); p.set("dstab", "director"); p.delete("view");
    window.location.search = p.toString();
  };

  return (
    <div className="border-2 border-black bg-[#0e0e0e] shadow-[3px_3px_0px_#000] p-3 flex flex-col gap-2.5" data-testid="place-director-bridge">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Clapperboard size={16} className="text-[#FFE08A] shrink-0" />
          <div className="min-w-0">
            <p className="font-mono text-[10px] font-black uppercase tracking-widest text-white truncate">Director · Scene {(context.scene_index ?? 0) + 1} plate</p>
            {context.scene_prompt ? <p className="font-mono text-[8px] text-white/45 truncate">“{context.scene_prompt}”</p> : null}
          </div>
        </div>
        <button onClick={saveAndReturn} disabled={!resultAssetId} data-testid="place-save-return"
          className="shrink-0 flex items-center gap-1.5 border-2 border-black bg-[#22C55E] text-black font-mono text-[9px] font-black uppercase tracking-widest px-3 py-2 shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
          <ArrowLeft size={13} /> Save & Return
        </button>
      </div>
      {(context.characters || []).length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="place-director-characters">
          {context.characters.map((c) => (
            <div key={c.character_id} className="flex items-center gap-1.5 bg-[#141414] border-2 border-[#C9B6FF]/40 px-1.5 py-1 shrink-0">
              {thumbs[c.character_id]
                ? <img src={thumbs[c.character_id]} alt={c.name} className="w-6 h-6 object-cover border border-black" />
                : <div className="w-6 h-6 grid place-items-center bg-black text-[#C9B6FF]"><User size={11} /></div>}
              <span className="font-mono text-[9px] text-white/80">{c.name}</span>
            </div>
          ))}
        </div>
      )}
      {!resultAssetId && <p className="font-mono text-[7px] text-white/40">Generate an image below, then tap Save &amp; Return to drop it onto the scene.</p>}
    </div>
  );
}

function PlaceFlow() {
  const [target, setTarget] = useState(null);
  const [targetSrc, setTargetSrc] = useState(null);
  const [objects, setObjects] = useState([]);   // [{id, file, src}]
  const [markers, setMarkers] = useState({});    // { [objIndex]: {x,y} }
  const [activeObj, setActiveObj] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const objInputRef = useRef(null);
  const imgWrapRef = useRef(null);

  const reset = () => {
    if (targetSrc) URL.revokeObjectURL(targetSrc);
    objects.forEach((o) => o.src && URL.revokeObjectURL(o.src));
    setTarget(null); setTargetSrc(null); setObjects([]); setMarkers({}); setActiveObj(null); setInstruction(""); setResult(null);
  };
  const pickTarget = (f) => {
    if (targetSrc) URL.revokeObjectURL(targetSrc);
    setTarget(f); setTargetSrc(URL.createObjectURL(f)); setResult(null); setMarkers({});
  };
  const clearTarget = () => { if (targetSrc) URL.revokeObjectURL(targetSrc); setTarget(null); setTargetSrc(null); setMarkers({}); };

  const addObject = (f) => {
    if (!f || !f.type?.startsWith("image/")) return;
    setObjects((prev) => {
      if (prev.length >= PLACE_MAX) { toast.warning(`Up to ${PLACE_MAX} objects`); return prev; }
      return [...prev, { id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, file: f, src: URL.createObjectURL(f) }];
    });
  };
  const onAddFiles = (e) => { Array.from(e.target.files || []).forEach(addObject); e.target.value = ""; };
  const removeObject = (i) => {
    setObjects((prev) => { const o = prev[i]; if (o?.src) URL.revokeObjectURL(o.src); return prev.filter((_, idx) => idx !== i); });
    setMarkers((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => { const n = +k; if (n < i) next[n] = v; else if (n > i) next[n - 1] = v; });
      return next;
    });
    setActiveObj(null);
  };

  const tapTarget = (e) => {
    if (!targetSrc || !imgWrapRef.current) return;
    if (objects.length === 0) { toast("Add a character or object first", { icon: "🧩" }); return; }
    const rect = imgWrapRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    let idx = activeObj;
    if (idx == null) {
      idx = objects.findIndex((_, i) => markers[i] == null);
      if (idx === -1) idx = objects.length - 1;
    }
    setMarkers((prev) => ({ ...prev, [idx]: { x, y } }));
    setActiveObj(null);
  };

  const placedCount = Object.keys(markers).length;

  const generate = async () => {
    if (!target) { toast.error("Add a target scene first"); return; }
    if (objects.length === 0) { toast.error("Add at least one object/character"); return; }
    setBusy(true); setResult(null); setRenderBusy(true);
    try {
      const markerArr = objects.map((_, i) => (markers[i] ? { index: i, x: markers[i].x, y: markers[i].y } : null)).filter(Boolean);
      const { job_id } = await pipelinesApi.placeObjects(target, objects.map((o) => o.file), markerArr, instruction.trim());
      const j = await pipelinesApi.poll(job_id);
      if (j.status === "completed" && j.asset_id) {
        setResult({ asset: { id: j.asset_id, track: j.track || "image" }, engine: j.engine });
        guardToast(j);
        toast.success("Placed ✓");
      } else { toast.error(j.error || "Placement failed"); }
    } catch (e) { toast.error(pipelineErr(e, "Placement failed")); }
    finally { setBusy(false); setRenderBusy(false); }
  };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-white" data-testid="create-place">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-3 space-y-4">
        <PlaceWorkspaceHeader resultAssetId={result?.asset?.id} />
        {/* Step 1 — target scene + tap-to-place */}
        {!targetSrc ? (
          <SwapUpload testid="place-target" label="[ 1. TARGET SCENE / BACKGROUND ]" src={null} onPick={pickTarget} Icon={ImagePlus} />
        ) : (
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="font-mono text-[8px] font-black uppercase tracking-[0.08em] text-black">[ 1. TAP THE SCENE TO PLACE · {placedCount}/{objects.length} ]</p>
              <button onClick={clearTarget} data-testid="place-target-clear"
                className="flex items-center gap-1 border-2 border-black bg-white px-1.5 py-0.5 font-mono text-[7px] font-black uppercase tracking-widest active:translate-y-[1px]"><X size={10} /> Change</button>
            </div>
            <div className="relative border-2 border-black bg-white p-1 shadow-[4px_4px_0px_#000]">
              <div ref={imgWrapRef} onClick={tapTarget} className="relative leading-[0] cursor-crosshair" data-testid="place-target-canvas">
                <img src={targetSrc} className="w-full h-auto block select-none" alt="target scene" draggable={false} />
                {objects.map((o, i) => markers[i] && (
                  <span key={o.id} data-testid={`place-marker-${i}`}
                    className="absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center bg-[#00F5D4] text-black font-mono text-[11px] font-black border-2 border-black rounded-full shadow-[2px_2px_0px_#000] pointer-events-none"
                    style={{ left: `${markers[i].x * 100}%`, top: `${markers[i].y * 100}%` }}>{i + 1}</span>
                ))}
              </div>
            </div>
            <p className="mt-1 text-center font-mono text-[8px] uppercase tracking-widest text-black/50">
              {activeObj != null ? `Aiming object ${activeObj + 1} — tap the scene` : "Tap a thumbnail to aim, then tap the scene"}
            </p>
          </div>
        )}

        {/* Step 2 — characters / objects */}
        <div>
          <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">[ 2. CHARACTERS / OBJECTS · {objects.length}/{PLACE_MAX} ]</p>
          <input ref={objInputRef} type="file" accept="image/*" multiple hidden onChange={onAddFiles} data-testid="place-object-input" />
          <div className="flex flex-wrap gap-2">
            {objects.map((o, i) => (
              <div key={o.id} className="relative" data-testid={`place-object-${i}`}>
                <button onClick={() => setActiveObj(activeObj === i ? null : i)}
                  className={`w-16 h-16 border-2 overflow-hidden ${activeObj === i ? "border-[#00F5D4] ring-2 ring-[#00F5D4]" : "border-black"}`}>
                  <img src={o.src} alt="" className="w-full h-full object-cover" />
                </button>
                <span className="absolute -top-1.5 -left-1.5 w-5 h-5 grid place-items-center bg-black text-[#00F5D4] font-mono text-[9px] font-black border-2 border-[#00F5D4] rounded-full">{i + 1}</span>
                {markers[i] && <span data-testid={`place-object-set-${i}`} className="absolute -bottom-1 -right-1 w-4 h-4 grid place-items-center bg-[#6BCB77] text-black rounded-full border border-black"><MapPin size={9} /></span>}
                <button onClick={() => removeObject(i)} data-testid={`place-object-remove-${i}`}
                  className="absolute -top-2 -right-2 w-5 h-5 grid place-items-center bg-rust text-white border-2 border-black rounded-full"><X size={10} /></button>
              </div>
            ))}
            {objects.length < PLACE_MAX && (
              <div className="flex flex-col gap-1">
                <button onClick={() => objInputRef.current?.click()} data-testid="place-add-object"
                  className="w-16 h-16 border-2 border-dashed border-black bg-white grid place-items-center active:scale-95"><Plus size={20} /></button>
                <VaultPickButton onFile={addObject} bucket="images" testid="place-object-vault"
                  className="w-16 flex items-center justify-center gap-1 border-2 border-black bg-black text-[#00F5D4] px-1 py-1 font-mono text-[7px] font-black uppercase tracking-widest active:translate-y-[1px]" />
              </div>
            )}
          </div>
        </div>

        {/* Step 3 — placement notes */}
        <div>
          <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">[ 3. PLACEMENT NOTES (OPTIONAL) ]</p>
          <textarea value={instruction} onChange={(e) => setInstruction(e.target.value.slice(0, 300))}
            data-testid="place-instruction" rows={2}
            placeholder="e.g. place image 1 (the character) walking in, keep image 2 smaller in the background"
            className="w-full bg-white text-black placeholder:text-black/40 border-2 border-black p-3 font-mono text-xs outline-none focus:ring-2 focus:ring-[#FFDE4D] shadow-[3px_3px_0px_#000] resize-none" />
        </div>

        <ImageResult result={result} />
      </div>
      <div className="shrink-0 border-t-2 border-black bg-white px-4 py-3 grid grid-cols-[auto_1fr] gap-2">
        <button onClick={reset} data-testid="place-reset"
          className="flex items-center justify-center gap-1 border-2 border-black bg-white text-black px-3 font-mono text-[10px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><RotateCcw size={14} /></button>
        <button onClick={generate} disabled={busy || !target || objects.length === 0} data-testid="place-generate"
          className="flex items-center justify-center gap-2 bg-[#FFDE4D] text-black font-black font-mono text-xs uppercase tracking-widest py-4 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} {busy ? "Placing…" : "➔ Generate Image"}
        </button>
      </div>
    </div>
  );
}

function SwapUpload({ testid, label, src, onPick, onClear, Icon }) {
  const ref = useRef(null);
  const pick = (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    onPick(f);
  };
  return (
    <div>
      <p className="font-mono text-[8px] font-black uppercase tracking-[0.08em] text-black mb-1">{label}</p>
      <input ref={ref} type="file" accept="image/*" hidden onChange={pick} data-testid={`${testid}-input`} />
      {src ? (
        <div className="relative border-2 border-black bg-white p-1 shadow-[4px_4px_0px_#000] aspect-square" data-testid={`${testid}-preview`}>
          <img src={src} alt={label} className="w-full h-full object-cover" />
          <button onClick={onClear} data-testid={`${testid}-clear`}
            className="absolute top-1 right-1 w-7 h-7 grid place-items-center border-2 border-black bg-white shadow-[2px_2px_0px_#000]"><X size={14} /></button>
        </div>
      ) : (
        <div className="space-y-2">
          <button onClick={() => ref.current?.click()} data-testid={testid}
            className="w-full aspect-square flex flex-col items-center justify-center gap-2 border-2 border-dashed border-black bg-white shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <Icon size={26} strokeWidth={2.2} className="text-black" />
            <span className="font-mono text-[9px] font-black uppercase tracking-widest text-black/50">Tap to drop</span>
          </button>
          <VaultPickButton onFile={(f) => onPick(f)} bucket="images" testid={`${testid}-vault`}
            className="w-full flex items-center justify-center gap-1.5 border-2 border-black bg-black text-[#00F5D4] px-3 py-1.5 font-mono text-[8px] font-black uppercase tracking-widest active:translate-y-[1px]" />
        </div>
      )}
    </div>
  );
}

function FaceSlot({ index, src, onPick, onClear }) {
  const inp = useRef(null);
  const pick = (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    onPick(f);
  };
  return (
    <div className="border-2 border-black bg-white p-2 shadow-[3px_3px_0px_#000]" data-testid={`faceslot-${index}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="w-5 h-5 grid place-items-center bg-[#00F5D4] text-black font-mono text-[10px] font-black border-2 border-black shrink-0">{index}</span>
        <span className="font-mono text-[8px] font-black uppercase tracking-widest text-black/60">Face {index}</span>
      </div>
      <input ref={inp} type="file" accept="image/*" hidden onChange={pick} data-testid={`faceslot-${index}-input`} />
      {src ? (
        <div className="relative border-2 border-black aspect-square">
          <img src={src} className="w-full h-full object-cover" alt={`source ${index}`} />
          <button onClick={onClear} data-testid={`faceslot-${index}-clear`}
            className="absolute top-0.5 right-0.5 w-6 h-6 grid place-items-center border-2 border-black bg-white shadow-[2px_2px_0px_#000]"><X size={12} /></button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <button onClick={() => inp.current?.click()} data-testid={`faceslot-${index}-pick`}
            className="w-full aspect-square flex flex-col items-center justify-center gap-1 border-2 border-dashed border-black bg-white active:translate-x-[1px] active:translate-y-[1px]">
            <ScanFace size={20} strokeWidth={2.2} className="text-black" />
            <span className="font-mono text-[7px] font-black uppercase tracking-widest text-black/50">Drop face</span>
          </button>
          <VaultPickButton onFile={(f) => onPick(f)} bucket="images" testid={`faceslot-${index}-vault`}
            className="w-full flex items-center justify-center gap-1 border-2 border-black bg-black text-[#00F5D4] px-2 py-1 font-mono text-[7px] font-black uppercase tracking-widest active:translate-y-[1px]" />
        </div>
      )}
    </div>
  );
}

function FaceSwapFlow() {
  const [target, setTarget] = useState(null);
  const [targetSrc, setTargetSrc] = useState(null);
  const [faces, setFaces] = useState(null);      // null = not run, [] normalized after detect
  const [detecting, setDetecting] = useState(false);
  const [sources, setSources] = useState({});    // index -> { file, src }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [restore, setRestore] = useState("crisp");
  const [beforeSrc, setBeforeSrc] = useState(null);

  const clearTarget = () => {
    if (targetSrc) URL.revokeObjectURL(targetSrc);
    Object.values(sources).forEach((s) => s?.src && URL.revokeObjectURL(s.src));
    setTargetSrc(null); setTarget(null); setFaces(null); setSources({}); setResult(null);
  };

  const pickTarget = async (f) => {
    if (targetSrc) URL.revokeObjectURL(targetSrc);
    Object.values(sources).forEach((s) => s?.src && URL.revokeObjectURL(s.src));
    setTargetSrc(URL.createObjectURL(f)); setTarget(f); setResult(null); setFaces(null); setSources({});
    setDetecting(true);
    try {
      const res = await pipelinesApi.detectFaces(f);
      let fs = res.faces || [];
      const detected = fs.length;
      if (detected === 0) fs = [{ index: 0, box: { x: 0, y: 0, w: 1, h: 1 }, whole: true }];
      setFaces(fs);
      if (detected === 0) toast("No distinct faces found — the whole photo is one swap target", { icon: "🧩" });
      else toast.success(`${detected} face${detected > 1 ? "s" : ""} detected`);
    } catch (e) {
      setFaces([{ index: 0, box: { x: 0, y: 0, w: 1, h: 1 }, whole: true }]);
      toast.error(pipelineErr(e, "Face detection failed"));
    } finally { setDetecting(false); }
  };

  const setSource = (index, f) => setSources((p) => {
    if (p[index]?.src) URL.revokeObjectURL(p[index].src);
    return { ...p, [index]: { file: f, src: URL.createObjectURL(f) } };
  });
  const clearSource = (index) => setSources((p) => {
    if (p[index]?.src) URL.revokeObjectURL(p[index].src);
    const n = { ...p }; delete n[index]; return n;
  });

  const mappedCount = (faces || []).filter((f) => sources[f.index]).length;

  const run = async () => {
    if (!target) { toast.error("Drop the target photo / scene first"); return; }
    const routes = (faces || []).filter((f) => sources[f.index]).map((f) => ({ index: f.index, box: f.box }));
    if (routes.length === 0) { toast.error("Drop a source face onto at least one target face"); return; }
    const sourceMap = {};
    routes.forEach((r) => { sourceMap[r.index] = sources[r.index].file; });
    setBusy(true); setResult(null); setRenderBusy(true);
    try {
      const { job_id } = await pipelinesApi.multiFaceSwap(target, routes, sourceMap, restore);
      const j = await pipelinesApi.poll(job_id);
      if (j.status === "completed" && j.asset_id) {
        setResult({ asset: { id: j.asset_id, track: j.track || "image" }, engine: j.engine });
        setBeforeSrc(targetSrc);
        guardToast(j);
        if (j.engine && j.engine.startsWith("fallback")) toast("Simulated swap — add a Fal/PiAPI key for photoreal output", { icon: "🧪" });
        else toast.success(`Swapped ${j.faces_swapped ?? routes.length} face${routes.length > 1 ? "s" : ""} ✓`);
      } else { toast.error("Face swap failed"); }
    } catch (e) { toast.error(pipelineErr(e, "Face swap failed")); }
    finally { setBusy(false); setRenderBusy(false); }
  };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-white" data-testid="create-faceswap">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-3 space-y-4">
        {/* Step 1 — target scene + face detection */}
        {!targetSrc ? (
          <SwapUpload testid="faceswap-target" label="[ 1. TARGET PHOTO / SCENE ]" src={null}
            onPick={pickTarget} Icon={ImagePlus} />
        ) : (
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="font-mono text-[8px] font-black uppercase tracking-[0.08em] text-black">[ 1. TARGET PHOTO / SCENE ]</p>
              <button onClick={clearTarget} data-testid="faceswap-target-clear"
                className="flex items-center gap-1 border-2 border-black bg-white px-1.5 py-0.5 font-mono text-[7px] font-black uppercase tracking-widest active:translate-y-[1px]"><X size={10} /> Change</button>
            </div>
            <div className="relative border-2 border-black bg-white p-1 shadow-[4px_4px_0px_#000]" data-testid="faceswap-target-preview">
              <div className="relative leading-[0]">
                <img src={targetSrc} className="w-full h-auto block" alt="target scene" />
                {(faces || []).filter((f) => !f.whole).map((f) => (
                  <div key={f.index} data-testid={`face-marker-${f.index}`}
                    className="absolute border-2 border-[#00F5D4] pointer-events-none"
                    style={{ left: `${f.box.x * 100}%`, top: `${f.box.y * 100}%`, width: `${f.box.w * 100}%`, height: `${f.box.h * 100}%`, boxShadow: "0 0 0 2px #000" }}>
                    <span className="absolute -top-2.5 -left-2.5 w-5 h-5 grid place-items-center bg-[#00F5D4] text-black font-mono text-[10px] font-black border-2 border-black">{f.index}</span>
                  </div>
                ))}
                {detecting && (
                  <div className="absolute inset-0 grid place-items-center bg-black/50" data-testid="faceswap-detecting">
                    <div className="flex items-center gap-2 border-2 border-black bg-[#FFDE4D] px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest">
                      <Loader2 size={14} className="animate-spin" /> Scanning faces…
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — numbered source drop-zones (one per detected face) */}
        {faces && !detecting && (
          <div data-testid="faceswap-routes">
            <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">
              {faces[0]?.whole ? "[ 2. FACE TO INSERT ]" : `[ 2. DROP A FACE ONTO EACH TARGET · ${mappedCount}/${faces.length} SET ]`}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {faces.map((f) => (
                <FaceSlot key={f.index} index={f.index} src={sources[f.index]?.src}
                  onPick={(file) => setSource(f.index, file)} onClear={() => clearSource(f.index)} />
              ))}
            </div>
          </div>
        )}

        {/* Restore Strength — CodeFormer/GFPGAN sharpening intensity. */}
        {faces && !detecting && (
          <div data-testid="restore-strength">
            <p className="font-mono text-[8px] font-black uppercase tracking-[0.14em] text-black/60 mb-1">Restore strength · face sharpening</p>
            <div className="grid grid-cols-2 gap-2">
              {[{ k: "natural", l: "Natural" }, { k: "crisp", l: "Crisp" }].map((r) => (
                <button key={r.k} onClick={() => setRestore(r.k)} data-testid={`restore-${r.k}`}
                  className={`border-2 border-black py-2 font-mono text-[9px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${restore === r.k ? "bg-[#FFDE4D] text-black" : "bg-white text-black/50"}`}>
                  {r.l}
                </button>
              ))}
            </div>
          </div>
        )}

        <ImageResult result={result} compareSrc={beforeSrc} />
      </div>
      <div className="shrink-0 border-t-2 border-black bg-white px-4 py-3" data-testid="faceswap-bar">
        <button onClick={run} disabled={busy || detecting || !target || mappedCount === 0} data-testid="faceswap-execute"
          className="w-full flex items-center justify-center gap-2 bg-[#FFDE4D] text-black font-black font-mono text-xs uppercase tracking-widest py-4 border-2 border-black rounded-none shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} {busy ? "Swapping…" : "➔ Swap and Fix Identity"}
        </button>
      </div>
    </div>
  );
}

/* --------------------------- Animate sub-flow --------------------------- */
const CAMERA_PRESETS = [
  { k: "zoom-in", label: "Zoom In", Icon: ZoomIn, prompt: ", with a cinematic slow zoom in" },
  { k: "pan-left", label: "Pan Left", Icon: MoveHorizontal, prompt: ", with a smooth pan to the left" },
  { k: "tilt-up", label: "Tilt Up", Icon: MoveVertical, prompt: ", with an upward camera tilt" },
  { k: "orbit", label: "Orbit", Icon: Orbit, prompt: ", with an orbiting camera movement" },
];
const DURATIONS = [6, 8, 10];
const ANIM_RATIOS = [{ k: "9:16", p: "Reels" }, { k: "1:1", p: "Posts" }, { k: "16:9", p: "Wide" }];
const EXPORTS = [{ k: "mp4", label: "MP4", Icon: Film }, { k: "gif", label: "GIF", Icon: ImageIcon }];
const AUDIO_PRESETS = [
  { k: "mute", label: "Mute", Icon: VolumeX },
  { k: "natural", label: "Natural Ambient", Icon: Wind },
  { k: "city", label: "City Bustle", Icon: Building2 },
  { k: "auto", label: "Auto-Match Scene", Icon: Wand2 },
];
const AR_CSS = { "9:16": "9 / 16", "1:1": "1 / 1", "16:9": "16 / 9" };

function AnimateFlow() {
  const [motion, setMotion] = useState("");
  const [strength, setStrength] = useState(50);
  const [camera, setCamera] = useState(null);
  const [loop, setLoop] = useState(false);
  const [duration, setDuration] = useState(6);
  const [ratio, setRatio] = useState("9:16");
  const [fmt, setFmt] = useState("mp4");
  const [audio, setAudio] = useState("auto");
  const [showBrushes, setShowBrushes] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [sourceFile, setSourceFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const soon = (f) => toast(`${f} — coming soon`, { icon: "🎬" });

  useEffect(() => () => { if (imgSrc) URL.revokeObjectURL(imgSrc); }, [imgSrc]);

  const onPickImage = (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    if (imgSrc) URL.revokeObjectURL(imgSrc);
    setImgSrc(URL.createObjectURL(f));
    setSourceFile(f);
    setResult(null);
  };

  const runAnimate = async () => {
    if (!sourceFile) { toast.error("Upload an image to animate"); return; }
    setBusy(true); setResult(null);
    try {
      const { job_id } = await pipelinesApi.animate(sourceFile, {
        motion_prompt: motion.trim(), strength, duration, audio_preset: audio,
      });
      const j = await pipelinesApi.poll(job_id);
      if (j.status === "completed" && j.asset_id) {
        setResult({ asset: { id: j.asset_id, track: j.track || "video" }, engine: j.engine });
        guardToast(j);
        if (j.warning) toast.warning(j.warning, { duration: 9000 });
        else toast.success(j.save_verified ? "Animation ready — saved to Video Vault ✓" : "Animation ready");
      } else {
        toast.error(j.error || "Animation failed");
      }
    } catch (e) {
      toast.error(pipelineErr(e, "Animate failed"));
    } finally { setBusy(false); }
  };

  // Camera preset → motion prompt sync (append/replace, never destroy typed text).
  const selectCamera = (k) => {
    const next = camera === k ? null : k;
    setCamera(next);
    setMotion((prev) => {
      let base = prev;
      for (const c of CAMERA_PRESETS) base = base.split(c.prompt).join("");
      const add = next ? (CAMERA_PRESETS.find((c) => c.k === next)?.prompt || "") : "";
      return (base + add).slice(0, 400);
    });
  };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="create-animate-flow">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-3 space-y-4">
        {/* 2.1 · Media upload / visual preview window */}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} data-testid="animate-file-input" />
        {imgSrc ? (
          <div className="w-full flex justify-center">
            <button onClick={() => fileRef.current?.click()} data-testid="animate-preview"
              style={{ aspectRatio: AR_CSS[ratio] }}
              className="relative h-[300px] border-2 border-black bg-charcoal shadow-[4px_4px_0px_#000] overflow-hidden">
              <img src={imgSrc} alt="to animate" className="w-full h-full object-cover" />
              <span onClick={(e) => { e.stopPropagation(); if (imgSrc) URL.revokeObjectURL(imgSrc); setImgSrc(null); }}
                data-testid="animate-preview-clear"
                className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center border-2 border-black bg-[#141414] shadow-[2px_2px_0px_#000]"><X size={14} /></span>
            </button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} data-testid="animate-preview"
            className="w-full flex flex-col items-center justify-center gap-2 border-2 border-black bg-[#141414] h-52 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <Camera size={32} className="text-white/50" strokeWidth={2.2} />
            <span className="font-mono text-[10px] font-black uppercase tracking-widest text-white/60">Tap to upload the image you want to animate</span>
          </button>
        )}
        {!imgSrc && (
          <div className="flex justify-center -mt-1">
            <VaultPickButton onFile={(f) => onPickImage({ target: { files: [f], value: "" } })} bucket="images" testid="animate-vault" label="Or pick from your Vault" />
          </div>
        )}

        {/* Motion prompt */}
        <Section label="Motion prompt" action={<MicDictate onText={(t) => setMotion((p) => (p ? `${p} ${t}` : t).slice(0, 400))} title="Speak the motion" testId="mic-animate-motion" />}>
          <textarea value={motion} onChange={(e) => setMotion(e.target.value.slice(0, 400))} rows={2} data-testid="animate-motion-prompt"
            placeholder="Describe the motion – camera movement, physics, environmental speed... or tap the mic"
            className="w-full bg-[#141414] text-white placeholder:text-white/40 border-2 border-black px-3 py-2 font-mono text-[11px] outline-none focus:ring-2 focus:ring-gold resize-none break-words" />
        </Section>

        {/* Camera movement (lazy-man shortcut → syncs into motion prompt) */}
        <Section label="Camera movement">
          <div className="grid grid-cols-2 gap-1.5" data-testid="animate-camera">
            {CAMERA_PRESETS.map((c) => (
              <button key={c.k} onClick={() => selectCamera(c.k)} data-testid={`animate-camera-${c.k}`}
                className={`flex items-center gap-2 border-2 border-black px-2.5 py-2.5 font-mono text-[10px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${camera === c.k ? "bg-[#9D4EDD] text-white" : "bg-[#141414] text-white/60"}`}>
                <c.Icon size={14} /> {c.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Motion strength */}
        <Section label="Motion strength">
          <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-white/60">
            <span className="flex items-center gap-1"><Gauge size={12} /> Subtle</span>
            <span>{strength}%</span>
            <span>Intense</span>
          </div>
          <input type="range" min="0" max="100" value={strength} onChange={(e) => setStrength(+e.target.value)} data-testid="animate-strength" className="w-full accent-[#9D4EDD]" />
        </Section>

        {/* 2.2 · Precision brushes — collapsed accordion */}
        <div>
          <button onClick={() => setShowBrushes((v) => !v)} data-testid="animate-brushes-toggle"
            className="w-full flex items-center justify-between gap-2 border-2 border-[#9D4EDD] bg-[#141414] px-3 py-3 font-mono text-[10px] font-black uppercase tracking-widest text-white shadow-[3px_3px_0px_#9D4EDD] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <span className="flex items-center gap-2"><Wrench size={14} /> Show precision adjustment brushes</span>
            <ChevronDown size={16} className={`transition-transform ${showBrushes ? "rotate-180" : ""}`} />
          </button>
          {showBrushes && (
            <div className="mt-3 space-y-4" data-testid="animate-brushes-panel">
              <Section label="Directional brush" badge="soon">
                <button onClick={() => soon("Directional brush")} data-testid="animate-brush"
                  className="w-full flex items-center gap-2 border-2 border-dashed border-black/40 bg-[#141414] px-3 py-3 font-mono text-[10px] font-black uppercase tracking-widest text-white/60">
                  <MoveUpRight size={15} /> Draw motion arrows on image <SoonBadge />
                </button>
              </Section>
              <Section label="Freeze / masking brush" badge="soon">
                <button onClick={() => soon("Masking brush")} data-testid="animate-mask"
                  className="w-full flex items-center gap-2 border-2 border-dashed border-black/40 bg-[#141414] px-3 py-3 font-mono text-[10px] font-black uppercase tracking-widest text-white/60">
                  <Snowflake size={15} /> Paint areas to keep still <SoonBadge />
                </button>
              </Section>
            </div>
          )}
        </div>

        <Section label="Loop">
          <button onClick={() => setLoop((v) => !v)} data-testid="animate-loop" className="w-full flex items-center gap-2">
            <span className={`w-10 h-6 border-2 border-black flex items-center px-0.5 ${loop ? "bg-gold justify-end" : "bg-[#141414] justify-start"}`}><span className="w-4 h-4 bg-charcoal border border-black" /></span>
            <span className="flex-1 text-left font-mono text-[9px] uppercase tracking-widest text-white/60 flex items-center gap-1"><Repeat size={12} /> Seamless loop</span>
          </button>
        </Section>

        {/* Duration */}
        <Section label="Duration" icon={Clock}>
          <div className="grid grid-cols-3 gap-1.5" data-testid="animate-durations">
            {DURATIONS.map((d) => (
              <button key={d} onClick={() => setDuration(d)} data-testid={`animate-duration-${d}`}
                className={`border-2 border-black py-2.5 font-mono text-[11px] font-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${duration === d ? "bg-gold" : "bg-[#141414]"}`}>{d}s</button>
            ))}
          </div>
        </Section>

        {/* Aspect ratio */}
        <Section label="Aspect ratio">
          <div className="grid grid-cols-3 gap-1.5" data-testid="animate-ratios">
            {ANIM_RATIOS.map((r) => (
              <button key={r.k} onClick={() => setRatio(r.k)} data-testid={`animate-ratio-${r.k}`}
                className={`flex flex-col items-center gap-0.5 border-2 border-black py-2 shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${ratio === r.k ? "bg-gold" : "bg-[#141414]"}`}>
                <span className="font-mono text-[11px] font-black text-white">{r.k}</span>
                <span className="font-mono text-[7px] uppercase tracking-wide text-white/50">{r.p}</span>
              </button>
            ))}
          </div>
        </Section>

        {/* 2.3 · Environmental audio (ElevenLabs) — above export */}
        <Section label="Environmental audio (ElevenLabs)">
          <div className="grid grid-cols-2 gap-1.5" data-testid="animate-audio">
            {AUDIO_PRESETS.map((a) => (
              <button key={a.k} onClick={() => setAudio(a.k)} data-testid={`animate-audio-${a.k}`}
                className={`flex items-center gap-2 border-2 border-black px-2.5 py-2.5 font-mono text-[10px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${audio === a.k ? "bg-[#9D4EDD] text-white" : "bg-[#141414] text-white/60"}`}>
                <a.Icon size={14} /> {a.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Export format */}
        <Section label="Export format">
          <div className="grid grid-cols-2 gap-1.5" data-testid="animate-formats">
            {EXPORTS.map((f) => (
              <button key={f.k} onClick={() => setFmt(f.k)} data-testid={`animate-format-${f.k}`}
                className={`flex items-center justify-center gap-2 border-2 border-black py-2.5 font-mono text-[11px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${fmt === f.k ? "bg-gold text-black" : "bg-[#141414] text-white/60"}`}>
                <f.Icon size={14} /> {f.label}
              </button>
            ))}
          </div>
        </Section>
        <ResultCard result={result} />
      </div>
      <div className="shrink-0 border-t-4 border-[#2a2a2a] bg-[#141414] px-4 py-3" data-testid="animate-bar">
        <button onClick={runAnimate} disabled={busy || !sourceFile} data-testid="animate-run"
          className="w-full flex items-center justify-center gap-2 bg-[#9D4EDD] text-white font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-black rounded-full shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} {busy ? "Animating…" : "Animate"}
        </button>
      </div>
    </div>
  );
}

function Section({ label, badge, icon: Icon, action, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon size={12} className="text-white/50" />}
        <span className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">{label}</span>
        {badge && <SoonBadge label={badge} />}
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {children}
    </div>
  );
}
function SoonBadge({ label = "soon" }) {
  return <span className="border border-black/40 bg-black px-1 py-0.5 font-mono text-[7px] font-black uppercase tracking-wide text-white/50">{label}</span>;
}
function Chip({ label, Icon, on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`flex items-center gap-1 border-2 border-black px-2.5 py-1.5 font-mono text-[10px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${on ? "bg-gold text-black" : "bg-[#141414] text-white/60"}`}>
      {Icon && <Icon size={11} />} {label}
    </button>
  );
}

/* ---------------------- Effects Matrix sub-flow (Stage 5) --------------- */
const EFFECT_MATRIX = [
  { k: "vhs_glitch", label: "VHS Glitch", accent: "#FF6BD6" },
  { k: "neo_chrome", label: "Neo-Chrome", accent: "#7DF9FF" },
  { k: "vintage_film", label: "Vintage Film", accent: "#E9B872" },
  { k: "cyber_glow", label: "Cyber Glow", accent: "#00F5D4" },
  { k: "retro_halftone", label: "Retro Halftone", accent: "#FFFFFF" },
  { k: "noir", label: "Noir", accent: "#B0B0B0" },
];

function EffectsMatrix() {
  const [imgSrc, setImgSrc] = useState(null);
  const [file, setFile] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingLib, setLoadingLib] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => () => { if (imgSrc) URL.revokeObjectURL(imgSrc); }, [imgSrc]);

  const onPick = (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    if (imgSrc) URL.revokeObjectURL(imgSrc);
    setImgSrc(URL.createObjectURL(f));
    setFile(f);
    setResult(null);
  };

  // Pull an existing image from the user's vault library into the workspace —
  // fetched as a real File so it feeds the same apply-effect pipeline.
  const onPickLibrary = async (item) => {
    setPickerOpen(false);
    setLoadingLib(true);
    try {
      const f = await fetchAssetFile(item.id, item.filename || `vault_${item.id}.png`);
      if (imgSrc) URL.revokeObjectURL(imgSrc);
      setImgSrc(URL.createObjectURL(f));
      setFile(f);
      setResult(null);
    } catch (e) {
      toast.error("Could not load that library image");
    } finally { setLoadingLib(false); }
  };

  const exportEffect = async () => {
    if (!file) { toast.error("Upload an image first"); return; }
    if (!active) { toast.error("Pick an effect from the matrix"); return; }
    setBusy(true); setResult(null);
    try {
      const { job_id } = await pipelinesApi.applyEffect(file, active);
      const j = await pipelinesApi.poll(job_id);
      if (j.status === "completed" && j.asset_id) {
        setResult({ asset: { id: j.asset_id, track: "image" }, engine: j.engine, filename: `fx_${active}.png` });
        toast.success("Effect baked & exported");
      } else { toast.error(j.error || "Export failed"); }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Export failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="h-full w-full flex flex-col bg-black" data-testid="effects-matrix">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-3 space-y-4">
        {/* Live view window */}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} data-testid="fx-file-input" />
        {imgSrc ? (
          <div className="relative border-[3px] border-[#00F5D4] shadow-[0_0_18px_rgba(0,245,212,0.4)] overflow-hidden bg-[#0d0d0d]" data-testid="fx-live-view">
            <img src={imgSrc} alt="preview" className={`w-full max-h-[300px] object-contain ${active ? `fx-${active}` : ""}`} data-testid="fx-live-image" />
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button onClick={() => setPickerOpen(true)} data-testid="fx-swap-library"
                className="w-8 h-8 grid place-items-center border-2 border-[#00F5D4] bg-black text-[#00F5D4]" title="Swap from library"><Layers size={15} /></button>
              <button onClick={() => fileRef.current?.click()} data-testid="fx-change-image"
                className="w-8 h-8 grid place-items-center border-2 border-[#00F5D4] bg-black text-[#00F5D4]" title="Upload new"><ImagePlus size={15} /></button>
            </div>
            {active && <span className="absolute bottom-2 left-2 font-mono text-[8px] font-black uppercase tracking-widest bg-[#00F5D4] text-black px-2 py-0.5">{active.replace("_", " ")}</span>}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5" data-testid="fx-source-picker">
            <button onClick={() => setPickerOpen(true)} data-testid="fx-pick-library"
              className="flex flex-col items-center justify-center gap-2 border-2 border-[#00F5D4] bg-[#0d0d0d] h-52 text-[#00F5D4] active:scale-[0.99]">
              {loadingLib ? <Loader2 size={30} className="animate-spin" /> : <Layers size={30} strokeWidth={2.2} />}
              <span className="font-mono text-[10px] font-black uppercase tracking-widest text-center px-2">{loadingLib ? "Loading…" : "Pick Library Image"}</span>
            </button>
            <button onClick={() => fileRef.current?.click()} data-testid="fx-upload"
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#00F5D4]/60 bg-[#0d0d0d] h-52 text-[#00F5D4]/80 active:scale-[0.99]">
              <Camera size={30} strokeWidth={2.2} />
              <span className="font-mono text-[10px] font-black uppercase tracking-widest text-center px-2">Upload from Device</span>
            </button>
          </div>
        )}

        {/* 🎬 EFFECTS MATRIX */}
        <div>
          <p className="font-mono text-[11px] font-black uppercase tracking-[0.16em] text-[#00F5D4] mb-2.5">🎬 Effects Matrix</p>
          <div className="grid grid-cols-3 gap-2.5" data-testid="fx-matrix-grid">
            {EFFECT_MATRIX.map((fx) => {
              const on = active === fx.k;
              return (
                <button key={fx.k} onClick={() => setActive(on ? null : fx.k)} data-testid={`fx-matrix-${fx.k}`}
                  className="aspect-square flex items-center justify-center p-2 text-center bg-[#0d0d0d] font-mono text-[9px] font-black uppercase tracking-wide leading-tight transition-all active:scale-95"
                  style={{
                    color: on ? "#000" : fx.accent,
                    background: on ? fx.accent : "#0d0d0d",
                    border: `2px solid ${fx.accent}`,
                    boxShadow: on ? `0 0 16px ${fx.accent}` : "3px 3px 0px #000",
                  }}>
                  {fx.label}
                </button>
              );
            })}
          </div>
        </div>

        {result && (
          <div className="border-[3px] border-[#00F5D4] bg-[#0d0d0d] p-2.5 shadow-[0_0_18px_rgba(0,245,212,0.35)]" data-testid="fx-result">
            <span className="font-mono text-[8px] uppercase tracking-widest bg-[#00F5D4] text-black px-2 py-0.5">{result.engine}</span>
            <ResultCard result={result} />
          </div>
        )}
      </div>
      <div className="shrink-0 border-t-2 border-[#00F5D4]/40 bg-black px-4 py-3">
        <button onClick={exportEffect} disabled={busy || !file || !active} data-testid="fx-export"
          className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-[#00F5D4] shadow-[0_0_18px_rgba(0,245,212,0.5)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-40">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} {busy ? "Baking Effect…" : "Export with Effect"}
        </button>
      </div>
      {pickerOpen && <VaultPickerModal onClose={() => setPickerOpen(false)} onPicked={onPickLibrary} />}
    </div>
  );
}

/* -------------------- Overlay Removal sub-flow (Stage 5) ---------------- */
/* Screenshot clean-plate now; a Reel/Video overlay stripper is coming next —
   the mode switch is laid out to hold both without a rebuild. */
const STRIP_MODES = [
  { k: "screenshot", label: "Screenshot", sub: "Erase UI overlays from a still", Icon: ImageIcon },
  { k: "reel", label: "Reel / Video", sub: "Strip overlays across frames", Icon: Film },
];

function OverlayRemovalFlow() {
  const [mode, setMode] = useState("screenshot");
  const [src, setSrc] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingLib, setLoadingLib] = useState(false);
  const fileRef = useRef(null);
  const isReel = mode === "reel";

  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);
  // Reset the picked media when switching modes (image vs video are distinct).
  const switchMode = (k) => {
    if (k === mode) return;
    if (src) URL.revokeObjectURL(src);
    setSrc(null); setFile(null); setResult(null); setMode(k);
  };

  const onPick = (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    const want = isReel ? "video/" : "image/";
    if (!f.type.startsWith(want)) { toast.error(isReel ? "Pick a video/reel file" : "Pick an image file"); return; }
    if (src) URL.revokeObjectURL(src);
    setSrc(URL.createObjectURL(f));
    setFile(f);
    setResult(null);
  };

  // Pull an existing image/video from the durable library into the workspace —
  // fetched as a real File so it feeds the same image-strip / video pipeline.
  const onPickLibrary = async (item) => {
    setPickerOpen(false);
    setLoadingLib(true);
    try {
      const f = await fetchAssetFile(item.id, item.filename || `vault_${item.id}.${isReel ? "mp4" : "png"}`);
      if (src) URL.revokeObjectURL(src);
      setSrc(URL.createObjectURL(f));
      setFile(f);
      setResult(null);
    } catch (e) {
      toast.error("Could not load that library item");
    } finally { setLoadingLib(false); }
  };

  const run = async () => {
    if (!file) { toast.error(isReel ? "Load a reel first" : "Load a screenshot first"); return; }
    setBusy(true); setResult(null);
    try {
      const { job_id } = isReel ? await pipelinesApi.videoStrip(file) : await pipelinesApi.imageStrip(file);
      // Video inpainting is heavy — give it a long, patient poll window.
      const j = await pipelinesApi.poll(job_id, isReel ? { interval: 5000, timeout: 540000 } : undefined);
      if (j.status === "completed" && j.asset_id) {
        setResult({
          asset: { id: j.asset_id, track: isReel ? "video" : "image" },
          engine: j.engine,
          filename: isReel ? `clean_reel_${Date.now()}.mp4` : `clean_${Date.now()}.png`,
        });
        if (j.engine === "local_telea") toast("Cleaned with the on-device eraser (add a Fal key for sharper results)", { icon: "🧽" });
        else toast.success(isReel ? "Reel overlays removed — clean plate ready" : "Overlays removed — clean plate ready");
      } else { toast.error(j.error || "Overlay removal failed"); }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Overlay removal failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="h-full w-full flex flex-col bg-black" data-testid="overlay-removal">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-3 space-y-4">
        {/* Mode switch — Screenshot (image) + Reel / Video (both live) */}
        <div className="grid grid-cols-2 gap-2.5" data-testid="strip-mode-switch">
          {STRIP_MODES.map((m) => {
            const on = mode === m.k;
            return (
              <button key={m.k} onClick={() => switchMode(m.k)} data-testid={`strip-mode-${m.k}`}
                className={`flex flex-col items-start gap-1 border-2 p-3 text-left shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform ${on ? "bg-[#00F5D4] border-[#00F5D4]" : "bg-[#0d0d0d] border-[#00F5D4]/50"}`}>
                <span className="flex items-center gap-1.5">
                  <m.Icon size={16} className={on ? "text-black" : "text-[#00F5D4]"} />
                  <span className={`font-mono text-[11px] font-black uppercase tracking-wide ${on ? "text-black" : "text-white"}`}>{m.label}</span>
                </span>
                <span className={`font-mono text-[8px] uppercase tracking-wide ${on ? "text-black/60" : "text-white/45"}`}>{m.sub}</span>
              </button>
            );
          })}
        </div>

        {/* Trust cue — shows if this task runs on the user's own key or coins */}
        <div className="flex justify-start" data-testid="strip-trust-cue">
          <KeyModePill />
        </div>

        <input ref={fileRef} type="file" accept={isReel ? "video/*" : "image/*"} hidden onChange={onPick} data-testid="strip-file-input" />
        {src ? (
          <div className="relative border-[3px] border-[#00F5D4] shadow-[0_0_18px_rgba(0,245,212,0.4)] overflow-hidden bg-[#0d0d0d]" data-testid="strip-live-view">
            {isReel
              ? <video src={src} controls muted loop playsInline className="w-full max-h-[300px] object-contain bg-black" data-testid="strip-live-video" />
              : <img src={src} alt="preview" className="w-full max-h-[300px] object-contain" data-testid="strip-live-image" />}
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button onClick={() => setPickerOpen(true)} data-testid="strip-swap-library"
                className="w-8 h-8 grid place-items-center border-2 border-[#00F5D4] bg-black text-[#00F5D4]" title="Swap from library"><Layers size={15} /></button>
              <button onClick={() => fileRef.current?.click()} data-testid="strip-change-media"
                className="w-8 h-8 grid place-items-center border-2 border-[#00F5D4] bg-black text-[#00F5D4]" title="Upload new"><ImagePlus size={15} /></button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5" data-testid="strip-source-picker">
            <button onClick={() => setPickerOpen(true)} data-testid="strip-pick-library"
              className="flex flex-col items-center justify-center gap-2 border-2 border-[#00F5D4] bg-[#0d0d0d] h-52 text-[#00F5D4] active:scale-[0.99]">
              {loadingLib ? <Loader2 size={30} className="animate-spin" /> : <Layers size={30} strokeWidth={2.2} />}
              <span className="font-mono text-[10px] font-black uppercase tracking-widest text-center px-2">{loadingLib ? "Loading…" : (isReel ? "Pick Library Reel" : "Pick Library Image")}</span>
            </button>
            <button onClick={() => fileRef.current?.click()} data-testid={isReel ? "strip-upload-reel" : "strip-upload"}
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#00F5D4]/60 bg-[#0d0d0d] h-52 text-[#00F5D4]/80 active:scale-[0.99]">
              {isReel ? <Film size={30} strokeWidth={2.2} /> : <Camera size={30} strokeWidth={2.2} />}
              <span className="font-mono text-[10px] font-black uppercase tracking-widest px-4 text-center">
                {isReel ? "Upload Reel (≤ 60s)" : "Upload from Device"}
              </span>
            </button>
          </div>
        )}
        <p className="font-mono text-[9px] text-white/45 leading-relaxed border-2 border-dashed border-white/10 p-2.5">
          {isReel
            ? "Auto-detects the right action rail + bottom caption block and reconstructs a clean video plate across every frame (Fal.ai). Best for short vertical reels."
            : "Auto-detects the right action rail, the bottom caption block and floating text/stickers, then rebuilds a clean background."}
        </p>

        {result && (
          <div className="border-[3px] border-[#00F5D4] bg-[#0d0d0d] p-2.5 shadow-[0_0_18px_rgba(0,245,212,0.35)]" data-testid="strip-result">
            <span className="font-mono text-[8px] uppercase tracking-widest bg-[#00F5D4] text-black px-2 py-0.5">{result.engine}</span>
            <ResultCard result={result} />
          </div>
        )}
      </div>
      <div className="shrink-0 border-t-2 border-[#00F5D4]/40 bg-black px-4 py-3">
        <button onClick={run} disabled={busy || !file} data-testid="strip-run"
          className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-[#00F5D4] shadow-[0_0_18px_rgba(0,245,212,0.5)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-40">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Eraser size={16} />} {busy ? (isReel ? "Stripping Reel…" : "Removing Overlays…") : "Remove Overlays"}
        </button>
        {isReel && busy && <p className="mt-2 text-center font-mono text-[8px] uppercase tracking-widest text-white/40">Video render can take a couple of minutes</p>}
      </div>
      {pickerOpen && (
        <VaultPickerModal
          onClose={() => setPickerOpen(false)}
          onPicked={onPickLibrary}
          bucket={isReel ? "videos" : "images"}
          title={isReel ? "Pick from Video Vault" : "Pick from Image Vault"}
        />
      )}
    </div>
  );
}

/* ---------------------- Effects Workspace (Filters + Text/Sticker) ------ */
function EffectsWorkspace() {
  const [tab, setTab] = useState("text"); // "text" | "filters"
  return (
    <div className="h-full w-full flex flex-col bg-black" data-testid="effects-workspace">
      <div className="shrink-0 grid grid-cols-2 gap-2 px-4 pt-3 pb-2" data-testid="effects-tabs">
        <button onClick={() => setTab("text")} data-testid="effects-tab-text"
          className={`flex items-center justify-center gap-2 border-2 px-2 py-3 font-mono text-[10px] font-black uppercase tracking-[0.1em] shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform ${tab === "text" ? "bg-[#00F5D4] text-black border-[#00F5D4]" : "bg-[#141414] text-white/60 border-black"}`}>
          <TypeIcon size={14} strokeWidth={2.4} /> Text & Stickers
        </button>
        <button onClick={() => setTab("filters")} data-testid="effects-tab-filters"
          className={`flex items-center justify-center gap-2 border-2 px-2 py-3 font-mono text-[10px] font-black uppercase tracking-[0.1em] shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform ${tab === "filters" ? "bg-[#00F5D4] text-black border-[#00F5D4]" : "bg-[#141414] text-white/60 border-black"}`}>
          <SlidersHorizontal size={14} strokeWidth={2.4} /> Filters
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === "text" ? <TextOverlayStudio /> : <EffectsMatrix />}
      </div>
    </div>
  );
}

/* ------------------------------ Reserved panel --------------------------- */
function ReservedPanel({ Icon, title, body }) {
  return (
    <div className="border-2 border-black rounded-none bg-[#141414] p-6 shadow-[3px_3px_0px_#000] flex flex-col items-center text-center">
      <span className="w-16 h-16 grid place-items-center border-2 border-black bg-gold shadow-[3px_3px_0px_#000] mb-4">
        <Icon size={30} strokeWidth={2.2} className="text-white" />
      </span>
      <h3 className="font-serif text-2xl text-white leading-none">{title}</h3>
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/50 mt-3 leading-relaxed">{body}</p>
    </div>
  );
}

/* --------------------------- Director Mode ------------------------------ */
const DIRECTOR_DURATIONS = [
  { k: 3, label: "15 sec", sub: "3 frames" },
  { k: 6, label: "30 sec", sub: "6 frames" },
];
const MAX_DIR_REFS = 5;   // Director Mode — up to 5 character/style reference images

function FramePreview({ assetId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let alive = true;
    if (assetId) {
      streamAsset(assetId).then((u) => { if (alive) { url = u; setSrc(u); } }).catch(() => {});
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [assetId]);
  return src
    ? <img src={src} alt="frame" className="w-full h-full object-cover" data-testid="director-frame-img" />
    : <div className="w-full h-full grid place-items-center bg-[#0d0d0d]"><Loader2 className="animate-spin text-[#F5C518]" size={18} /></div>;
}

function DirectorFlow({ onBack }) {
  const [frameCount, setFrameCount] = useState(3);
  const [concept, setConcept] = useState(() => consumeDirectorPrompt());
  const [keys, setKeys] = useState(null);
  const [keyErr, setKeyErr] = useState("");
  const [checking, setChecking] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const [frames, setFrames] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null); // {done, total, stage}
  const [netStatus, setNetStatus] = useState(null); // subtle reconnect status
  const [result, setResult] = useState(null);
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const [refs, setRefs] = useState([]);            // up to 5 [{id, file, src}]
  const refInputRef = useRef(null);
  const [refAdherence, setRefAdherence] = useState(40); // 0 loose … 100 strict
  const [saved, setSaved] = useState([]);
  const [genJobId, setGenJobId] = useState(null);
  const cancelRef = useRef(false);
  const [tracks, setTracks] = useState([]);
  const [scoring, setScoring] = useState(false);

  // Map adherence slider → Flux i2i denoise strength (strict = lower deviation).
  const adherenceToStrength = (a) => +(0.9 - (a / 100) * 0.45).toFixed(3); // 0→0.9, 100→0.45

  const loadSaved = async () => {
    try { const r = await pipelinesApi.directorStoryboards(); setSaved(r.storyboards || []); }
    catch { /* noop */ }
  };
  const loadTracks = async () => {
    try { const r = await pipelinesApi.directorMusicTracks(); setTracks(r.tracks || []); }
    catch { /* noop */ }
  };
  useEffect(() => { loadSaved(); loadTracks(); }, []);

  const scoreReel = async (trackId, title) => {
    if (!result?.asset?.id) return;
    setScoring(true);
    try {
      const r = await pipelinesApi.directorScore(result.asset.id, trackId);
      if (r.asset_id) {
        setResult({ asset: { id: r.asset_id, track: "video" }, engine: r.engine });
        toast.success(`Scored with "${title}" ♫`);
      } else { toast.error("Scoring failed"); }
    } catch (e) { toast.error(pipelineErr(e, "Scoring failed")); }
    finally { setScoring(false); }
  };

  const onPickRef = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const imgs = files.filter((f) => f.type.startsWith("image/"));
    if (imgs.length < files.length) toast.error("Only image files are supported");
    setRefs((prev) => {
      const room = MAX_DIR_REFS - prev.length;
      if (room <= 0) { toast.error(`Up to ${MAX_DIR_REFS} reference images`); return prev; }
      if (imgs.length > room) toast.error(`Up to ${MAX_DIR_REFS} reference images`);
      const add = imgs.slice(0, room).map((f) => ({
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        file: f, src: URL.createObjectURL(f),
      }));
      return [...prev, ...add];
    });
  };
  const addRefFile = (f) => {
    if (!f || !f.type?.startsWith("image/")) return;
    setRefs((prev) => {
      if (prev.length >= MAX_DIR_REFS) { toast.error(`Up to ${MAX_DIR_REFS} reference images`); return prev; }
      return [...prev, { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, file: f, src: URL.createObjectURL(f) }];
    });
  };
  const removeRef = (id) => setRefs((prev) => {
    const t = prev.find((r) => r.id === id);
    if (t?.src) URL.revokeObjectURL(t.src);
    return prev.filter((r) => r.id !== id);
  });

  const checkKeys = async () => {
    setChecking(true); setKeyErr("");
    try { const k = await pipelinesApi.directorValidate(); setKeys(k); }
    catch (e) { setKeys(null); setKeyErr(pipelineErr(e, "Add your API keys in the Vault to continue.")); }
    finally { setChecking(false); }
  };
  useEffect(() => { checkKeys(); }, []);

  const draft = async (audioBlob) => {
    if (!audioBlob && !concept.trim()) { toast.error("Describe your scene or record your voice"); return; }
    setDrafting(true); setResult(null); setFrames([]);
    try {
      const strength = refs.length ? adherenceToStrength(refAdherence) : undefined;
      const { job_id } = await pipelinesApi.directorBlueprint(concept, frameCount, audioBlob, refs.map((r) => r.file), strength);
      const j = await pipelinesApi.directorStream(job_id, { timeout: 240000 });
      if (j.status === "completed" && j.storyboard?.frames?.length) {
        setFrames(j.storyboard.frames);
        if (j.storyboard.concept && !concept.trim()) setConcept(j.storyboard.concept);
        toast.success("Storyboard drafted — 0 Credits");
      } else { toast.error(j.error || "Storyboard drafting failed"); }
    } catch (e) { toast.error(pipelineErr(e, "Drafting failed")); }
    finally { setDrafting(false); }
  };

  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); return; }
    if (!keys) { toast.error("Add your OpenAI key first"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        await draft(new Blob(chunksRef.current, { type: "audio/webm" }));
      };
      recRef.current = mr; mr.start(); setRecording(true);
    } catch { toast.error("Microphone unavailable"); }
  };

  const updateMotion = (i, v) =>
    setFrames((f) => f.map((fr, idx) => (idx === i ? { ...fr, motion_prompt: v } : fr)));

  const saveStoryboard = async () => {
    if (!frames.length) return;
    try {
      await pipelinesApi.directorSaveStoryboard({
        frames, frame_count: frameCount, concept, has_reference: refs.length > 0,
        title: (concept || "").trim().slice(0, 60) || "Untitled Storyboard",
      });
      toast.success("Storyboard saved to Vault");
      loadSaved();
    } catch (e) { toast.error(pipelineErr(e, "Save failed")); }
  };

  const openSaved = async (sid) => {
    try {
      const r = await pipelinesApi.directorLoadStoryboard(sid);
      const sb = r.storyboard || {};
      if (sb.frames?.length) {
        setFrames(sb.frames);
        setFrameCount(sb.frame_count || sb.frames.length);
        setConcept(sb.concept || "");
        setResult(null);
        toast.success("Storyboard loaded — ready to generate");
      }
    } catch (e) { toast.error(pipelineErr(e, "Could not load storyboard")); }
  };

  const deleteSaved = async (sid, e) => {
    e.stopPropagation();
    try { await pipelinesApi.directorDeleteStoryboard(sid); loadSaved(); toast("Storyboard removed"); }
    catch { toast.error("Delete failed"); }
  };

  const cancelRender = async () => {
    cancelRef.current = true;
    if (genJobId) { try { await pipelinesApi.directorCancel(genJobId); } catch { /* noop */ } }
  };

  const ACTIVE_KEY = "director_active_job";

  // Poll a running render to completion + handle the terminal state. Shared by
  // generate() and the reconnect effect so a render survives leaving the page.
  const trackJob = async (job_id) => {
    setGenJobId(job_id);
    try {
      const j = await pipelinesApi.directorStream(job_id, {
        shouldStop: () => cancelRef.current,
        onTick: (t) => {
          if (t.total_frames) setProgress({ done: t.completed_frames || 0, total: t.total_frames, stage: t.stage });
        },
        onNetHiccup: (n) => setNetStatus(n > 0 ? "Connection hiccup — reconnecting…" : null),
      });
      if (j.status === "cancelled" || cancelRef.current) {
        toast("Render cancelled — any held Credits refunded", { icon: "↩️" });
      } else if (j.status === "completed" && j.asset_id) {
        setResult({ asset: { id: j.asset_id, track: j.track || "video" }, engine: j.engine });
        guardToast(j);
        if (j.save_verified) toast.success("Reel rendered — saved to Video Vault ✓");
      } else { toast.error(j.error || "Scene render failed"); }
    } catch (e) { toast.error(pipelineErr(e, "Render failed")); }
    finally {
      setGenerating(false); setProgress(null); setGenJobId(null); setNetStatus(null);
      try { localStorage.removeItem(ACTIVE_KEY); } catch { /* noop */ }
      try { window.dispatchEvent(new CustomEvent("mist:director-render", { detail: { active: false } })); } catch { /* noop */ }
    }
  };

  // Reconnect to an in-flight render after navigating away / reloading the page:
  // the render keeps running server-side, so we resume its progress + result.
  useEffect(() => {
    let raw;
    try { raw = localStorage.getItem(ACTIVE_KEY); } catch { raw = null; }
    if (!raw) return;
    let saved;
    try { saved = JSON.parse(raw); } catch { return; }
    if (!saved?.jobId || Date.now() - (saved.ts || 0) > 45 * 60 * 1000) {
      try { localStorage.removeItem(ACTIVE_KEY); } catch { /* noop */ }
      return;
    }
    cancelRef.current = false;
    if (Array.isArray(saved.frames) && saved.frames.length) {
      setFrames(saved.frames);
      setFrameCount(saved.frameCount || saved.frames.length);
    }
    setGenerating(true);
    setProgress({ done: 0, total: saved.frameCount || (saved.frames?.length || 3), stage: null });
    try { window.dispatchEvent(new CustomEvent("mist:director-render", { detail: { active: true } })); } catch { /* noop */ }
    toast("Reconnecting to your render…", { icon: "🎬" });
    trackJob(saved.jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    if (!frames.length) return;
    if (!keys?.video_ready) { toast.error("Add a FAL.ai or MiniMax key to render video"); return; }
    cancelRef.current = false;
    setGenerating(true); setResult(null);
    setProgress({ done: 0, total: frames.length, stage: null });
    try { window.dispatchEvent(new CustomEvent("mist:director-render", { detail: { active: true } })); } catch { /* noop */ }
    let jobId;
    try {
      const payload = frames.map((f) => ({
        preview_asset_id: f.preview_asset_id || "", image_prompt: f.image_prompt || "",
        motion_prompt: f.motion_prompt || "", title: f.title || "",
      }));
      const res = await pipelinesApi.directorGenerate(payload, frameCount);
      jobId = res.job_id;
      // Persist so the render survives leaving the page (reconnect on return).
      try { localStorage.setItem(ACTIVE_KEY, JSON.stringify({ jobId, frameCount, frames, ts: Date.now() })); } catch { /* noop */ }
    } catch (e) {
      toast.error(pipelineErr(e, "Could not start render"));
      setGenerating(false); setProgress(null);
      try { window.dispatchEvent(new CustomEvent("mist:director-render", { detail: { active: false } })); } catch { /* noop */ }
      return;
    }
    await trackJob(jobId);
  };

  if (checking) {
    return (
      <div className="h-full grid place-items-center" data-testid="director-checking">
        <Loader2 className="animate-spin text-[#F5C518]" size={26} />
      </div>
    );
  }
  if (keyErr || !keys) {
    return (
      <div className="h-full overflow-y-auto no-scrollbar px-4 py-6" data-testid="director-key-guard">
        {onBack && (
          <button onClick={onBack} data-testid="director-back-hub"
            className="mb-4 flex items-center gap-1.5 border-2 border-black bg-[#141414] text-white px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
            <ChevronLeft size={13} /> Director Hub
          </button>
        )}
        <div className="border-2 border-black bg-[#FF5A5A] p-4 shadow-[4px_4px_0px_#000]">
          <div className="flex items-center gap-2 mb-2"><KeyRound size={18} className="text-black" /><p className="font-serif text-lg text-black">Keys required</p></div>
          <p className="font-mono text-[11px] text-black/80 leading-relaxed" data-testid="director-key-error">{keyErr}</p>
        </div>
        <p className="font-mono text-[10px] text-white/60 mt-4 leading-relaxed">
          Director Mode is strictly 100% bring-your-own-key. Save your OpenAI key (and a FAL.ai or MiniMax key) in the Secure Vault → System Credentials, then retry.
        </p>
        <button onClick={checkKeys} data-testid="director-retry-keys"
          className="mt-4 w-full border-2 border-black bg-[#F5C518] py-3 font-serif text-lg text-black shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none">
          Re-check keys
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="director-flow">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-3 space-y-4">
        {onBack && (
          <button onClick={onBack} data-testid="director-back-hub"
            className="flex items-center gap-1.5 border-2 border-black bg-[#141414] text-white px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
            <ChevronLeft size={13} /> Director Hub
          </button>
        )}
        <div className="flex flex-wrap gap-2" data-testid="director-key-chips">
          {[["OpenAI", keys.openai], ["FAL.ai", keys.fal], ["MiniMax", keys.minimax]].map(([n, on]) => (
            <span key={n} data-testid={`director-key-${n.toLowerCase().replace(".", "")}`}
              className={`font-mono text-[9px] uppercase tracking-wide border-2 border-black px-2 py-1 shadow-[2px_2px_0px_#000] ${on ? "bg-[#9EFFB8] text-black" : "bg-[#1a1a1a] text-white/50"}`}>
              {on ? "✓" : "○"} {n}
            </span>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/50">Character / Style References (Optional)</p>
            <span className="font-mono text-[8px] text-white/40" data-testid="director-ref-count">{refs.length}/{MAX_DIR_REFS}</span>
          </div>
          <input ref={refInputRef} type="file" accept="image/*" multiple onChange={onPickRef} className="hidden" data-testid="director-ref-input" />

          <div className="grid grid-cols-3 gap-2" data-testid="director-ref-grid">
            {refs.map((r, i) => (
              <div key={r.id} className="relative aspect-square border-2 border-[#F5C518] bg-charcoal shadow-[3px_3px_0px_#F5C518] overflow-hidden" data-testid={`director-ref-item-${i}`}>
                <img src={r.src} alt={`reference ${i + 1}`} className="w-full h-full object-cover" />
                <button onClick={() => removeRef(r.id)} data-testid={`director-ref-remove-${i}`}
                  className="absolute top-1 right-1 w-6 h-6 grid place-items-center border-2 border-black bg-[#FF5A5A] text-black shadow-[2px_2px_0px_#000]">
                  <X size={12} />
                </button>
              </div>
            ))}
            {refs.length < MAX_DIR_REFS && (
              <button onClick={() => refInputRef.current?.click()} data-testid="director-ref-add"
                className="aspect-square flex flex-col items-center justify-center gap-1 border-2 border-dashed border-white/25 bg-[#0d0d0d] text-white/50 hover:border-[#F5C518] hover:text-[#F5C518] transition-colors">
                <ImagePlus size={20} />
                <span className="font-mono text-[8px] uppercase">Add</span>
              </button>
            )}
          </div>

          {refs.length < MAX_DIR_REFS && (
            <div className="mt-2 flex justify-center">
              <VaultPickButton
                onFile={(f) => addRefFile(f)}
                bucket="images" testid="director-ref-vault" label="Or pick from your Vault" />
            </div>
          )}

          {refs.length > 0 && (
            <div className="mt-3 border-2 border-[#F5C518]/40 bg-charcoal p-2" data-testid="director-ref-strength">
              <p className="font-mono text-[9px] text-[#F5C518] mb-2">Every frame will inherit {refs.length > 1 ? "these looks" : "this look"} ✓</p>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[8px] uppercase tracking-wide text-white/50">Reference adherence</span>
                <span className="font-mono text-[8px] text-[#F5C518]">{refAdherence >= 66 ? "Strict" : refAdherence >= 33 ? "Balanced" : "Loose"}</span>
              </div>
              <input type="range" min="0" max="100" step="5" value={refAdherence}
                onChange={(e) => setRefAdherence(Number(e.target.value))}
                data-testid="director-ref-adherence"
                className="w-full accent-[#F5C518] h-1.5" />
            </div>
          )}
        </div>

        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/50 mb-2">Scene length</p>
          <div className="grid grid-cols-2 gap-2" data-testid="director-duration">
            {DIRECTOR_DURATIONS.map((d) => {
              const on = frameCount === d.k;
              return (
                <button key={d.k} onClick={() => setFrameCount(d.k)} data-testid={`director-dur-${d.k}`}
                  className={`border-2 border-black p-3 text-left shadow-[3px_3px_0px_#000] transition-transform active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${on ? "bg-[#F5C518]" : "bg-[#1a1a1a]"}`}>
                  <p className={`font-serif text-2xl leading-none ${on ? "text-black" : "text-white"}`}>{d.label}</p>
                  <p className={`font-mono text-[9px] uppercase mt-1 ${on ? "text-black/60" : "text-white/50"}`}>{d.sub} · 5s each</p>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/50">Your scene concept</p>
            <MicDictate onText={(t) => setConcept((p) => (p ? `${p} ${t}` : t).slice(0, MAX_CHARS))} disabled={drafting} title="Speak your scene" testId="mic-director-concept" />
          </div>
          <textarea value={concept} onChange={(e) => setConcept(e.target.value.slice(0, MAX_CHARS))}
            data-testid="director-concept" rows={3} disabled={drafting}
            placeholder="Describe the story you want to see, or tap the mic to speak it. e.g. A lone astronaut walking across a neon Martian desert at dusk…"
            className="w-full bg-[#0d0d0d] border-2 border-black p-3 font-mono text-[12px] text-white placeholder-white/30 outline-none resize-none shadow-[3px_3px_0px_#000]" />
          <button onClick={() => draft(null)} disabled={drafting || !concept.trim()} data-testid="director-draft"
            className="mt-2 w-full flex items-center justify-center gap-2 border-2 border-black bg-[#00F5D4] py-3 font-serif text-base text-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-40">
            {drafting ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />} Draft · Free
          </button>
        </div>

        {saved.length > 0 && (
          <div data-testid="director-saved">
            <div className="flex items-center gap-1.5 mb-2">
              <FolderOpen size={12} className="text-white/50" />
              <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/50">Saved storyboards</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {saved.map((s) => (
                <div key={s.id} onClick={() => openSaved(s.id)} data-testid={`director-saved-${s.id}`}
                  className="group flex items-center gap-1.5 border-2 border-black bg-[#1a1a1a] pl-2.5 pr-1 py-1.5 shadow-[2px_2px_0px_#000] cursor-pointer hover:bg-[#242424]">
                  <span className="font-mono text-[10px] text-white truncate max-w-[130px]">{s.title || "Untitled"}</span>
                  <span className="font-mono text-[8px] text-[#F5C518]">{s.frame_count}f</span>
                  <button onClick={(e) => deleteSaved(s.id, e)} data-testid={`director-saved-del-${s.id}`}
                    className="w-5 h-5 grid place-items-center text-white/40 hover:text-[#FF5A5A]"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {frames.length > 0 && (
          <div data-testid="director-storyboard">
            <div className="flex items-center justify-between mb-2">
              <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/50">Storyboard · {frames.length} frames</p>
              <button onClick={saveStoryboard} disabled={generating} data-testid="director-save"
                className="flex items-center gap-1 border-2 border-black bg-[#9EFFB8] px-2 py-1 font-mono text-[9px] uppercase text-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-40">
                <Save size={11} /> Save
              </button>
            </div>
            <div className="space-y-3">
              {frames.map((f, i) => (
                <div key={i} data-testid={`director-frame-${i}`} className="border-2 border-black bg-[#141414] shadow-[3px_3px_0px_#000] overflow-hidden">
                  <div className="flex">
                    <div className="w-[92px] shrink-0 aspect-[9/16] border-r-2 border-black"><FramePreview assetId={f.preview_asset_id} /></div>
                    <div className="flex-1 min-w-0 p-2.5">
                      <p className="font-serif text-sm text-[#F5C518] leading-tight truncate">{i + 1}. {f.title}</p>
                      <p className="font-mono text-[10px] text-white/60 mt-1 leading-snug line-clamp-2">{f.scene}</p>
                      <div className="flex items-center gap-1 mt-2">
                        <Video size={11} className="text-white/40 shrink-0" />
                        <input value={f.motion_prompt || ""} onChange={(e) => updateMotion(i, e.target.value)}
                          data-testid={`director-motion-${i}`}
                          className="flex-1 min-w-0 bg-[#0d0d0d] border border-black px-2 py-1 font-mono text-[10px] text-white outline-none" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {generating && progress && (
              <div className="mt-4 border-2 border-black bg-[#141414] p-3 shadow-[3px_3px_0px_#000]" data-testid="director-progress">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-[#F5C518]">Rendering</p>
                  <p className="font-mono text-[9px] text-white/60" data-testid="director-progress-caption">
                    {progress.stage === "muxing" || progress.done >= progress.total
                      ? "Finalizing reel…"
                      : `Frame ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…`}
                  </p>
                </div>
                <div className="flex gap-1" data-testid="director-progress-bar">
                  {Array.from({ length: progress.total }).map((_, i) => {
                    const filled = i < progress.done;
                    const active = i === progress.done && progress.stage !== "muxing";
                    return (
                      <div key={i} data-testid={`director-progress-seg-${i}`}
                        className={`flex-1 h-2.5 border border-black ${filled ? "bg-[#F5C518]" : active ? "bg-[#F5C518]/40 animate-pulse" : "bg-[#0d0d0d]"}`} />
                    );
                  })}
                </div>
                <button onClick={cancelRender} data-testid="director-cancel"
                  className="mt-3 w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FF5A5A] py-2 font-serif text-sm text-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                  <Ban size={14} /> Cancel &amp; refund Credits
                </button>
                {netStatus && (
                  <p className="mt-2 flex items-center justify-center gap-1 font-mono text-[9px] text-[#F5C518]/80" data-testid="director-net-status">
                    <Loader2 size={10} className="animate-spin" /> {netStatus}
                  </p>
                )}
              </div>
            )}

            <button onClick={generate} disabled={generating || !keys.video_ready} data-testid="director-generate"
              className="mt-4 w-full flex items-center justify-center gap-2 border-2 border-black bg-[#F5C518] py-4 font-serif text-xl text-black shadow-[5px_5px_0px_#000] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none disabled:opacity-50">
              {generating ? <><Loader2 className="animate-spin" size={18} /> Rendering scene…</> : <><Film size={18} /> Generate Scene · 3 Credits</>}
            </button>
            {!keys.video_ready && (
              <p className="font-mono text-[9px] text-[#FF5A5A] mt-2 text-center" data-testid="director-video-warning">
                Add a FAL.ai or MiniMax key to enable video rendering.
              </p>
            )}
            <p className="font-mono text-[9px] text-white/40 mt-2 text-center flex items-center justify-center gap-1">
              <Clock3 size={10} /> Rendering runs on YOUR keys and can take a few minutes.
            </p>
          </div>
        )}

        {result && (
          <div data-testid="director-result">
            <ResultCard result={result} />
            {tracks.length > 0 && (
              <div className="mt-3" data-testid="director-musicbed">
                <div className="flex items-center gap-1.5 mb-2">
                  <Music2 size={12} className="text-[#F5C518]" />
                  <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/50">Add a music bed — instant scored cut</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tracks.map((t) => (
                    <button key={t.track_id} onClick={() => scoreReel(t.track_id, t.title)} disabled={scoring}
                      data-testid={`director-track-${t.track_id}`}
                      className="flex items-center gap-1.5 border-2 border-black bg-[#1a1a1a] px-2.5 py-1.5 shadow-[2px_2px_0px_#000] hover:bg-[#242424] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-40">
                      <Music2 size={11} className="text-[#F5C518] shrink-0" />
                      <span className="font-mono text-[10px] text-white truncate max-w-[140px]">{t.title}</span>
                    </button>
                  ))}
                </div>
                {scoring && (
                  <p className="font-mono text-[9px] text-[#F5C518] mt-2 flex items-center gap-1" data-testid="director-scoring">
                    <Loader2 className="animate-spin" size={10} /> Scoring your reel…
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= MANUAL GAME DEMO — movement macros ================= */
// The demo now RECORDS the local RoadEngine canvas (no cloud render). Each macro
// drives the squad's autopilot motion. Sprites (hero/trooper/villain) are drawn
// into the engine; the 6s clip is recorded and transcoded to mp4 (Game 6s album).
const GAME_MOVE_MACROS = [
  { k: "forward", label: "Forward March", Icon: ArrowUp, accent: "#00F5D4" },
  { k: "strafe_l", label: "Strafe Left", Icon: ArrowLeft, accent: "#A4DCFA" },
  { k: "strafe_r", label: "Strafe Right", Icon: ArrowRight, accent: "#c084fc" },
];

// Designer spawn-rule recipes — control what the 6s loop generates.
const SPAWN_RULES = [
  { k: "STANDARD", label: "Standard", emoji: "🎲", accent: "#94a3b8" },
  { k: "BLITZ", label: "Multiplier Blitz", emoji: "🟢", accent: "#00F5D4" },
  { k: "CORRIDOR", label: "Hazard Corridor", emoji: "🔴", accent: "#ff5570" },
  { k: "LOOT", label: "Loot Run", emoji: "📦", accent: "#FFD028" },
  { k: "BOSS", label: "Alpha Goliath", emoji: "👾", accent: "#c084fc" },
];

// Mission backdrop templates — set the correct gameplay angle so gates/barrels/
// troops overlay onto a real road/corridor surface (the image IS the road).
const MISSION_BGS = [
  { k: "neon", label: "Neon Grid", sub: "Procedural", url: null },
  { k: "road", label: "Canyon Road", sub: "Road Shooter", url: "/assets/game/road_shooter_bg.png" },
  { k: "corridor", label: "Sci-Fi Corridor", sub: "Corridor Shooter", url: "/assets/game/corridor_shooter_bg.png" },
];

// Compact segmented control for placeholder game-type settings.
function GDSeg({ label, value, onChange, options, tid }) {
  return (
    <div data-testid={tid}>
      <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1.5">{label}</p>
      <div className="grid grid-cols-3 gap-1.5">
        {options.map((o) => (
          <button key={o.v} onClick={() => onChange(o.v)} data-testid={`${tid}-${o.v}`}
            className={`border-2 border-black py-2 px-1 font-mono text-[8px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${value === o.v ? "bg-[#00F5D4] text-black" : "bg-[#141414] text-white/60"}`}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

// A single sprite upload slot (module scope so it never remounts on each render).
function GDSlot({ label, val, onSet, Icon, tid }) {
  const ref = useRef(null);
  const pick = (e) => { const f = (e.target.files || [])[0]; if (e.target) e.target.value = ""; if (!f) return; if (!f.type.startsWith("image/")) { toast.error("Pick an image"); return; } if (val?.src) URL.revokeObjectURL(val.src); onSet({ file: f, src: URL.createObjectURL(f) }); };
  return (
    <div data-testid={tid}>
      <p className="font-mono text-[8px] font-black uppercase tracking-widest text-white/60 mb-1">{label}</p>
      <input ref={ref} type="file" accept="image/*" hidden data-testid={`${tid}-input`} onChange={pick} />
      {val ? (
        <div className="relative aspect-square border-2 border-black bg-black overflow-hidden">
          <img src={val.src} alt={label} className="w-full h-full object-cover" />
          <button onClick={() => { if (val.src) URL.revokeObjectURL(val.src); onSet(null); }} data-testid={`${tid}-clear`} className="absolute top-0.5 right-0.5 w-6 h-6 grid place-items-center border-2 border-black bg-[#141414] text-white"><X size={11} /></button>
        </div>
      ) : (
        <button onClick={() => ref.current?.click()} data-testid={`${tid}-pick`} className="w-full aspect-square flex flex-col items-center justify-center gap-1 border-2 border-dashed border-white/25 bg-[#141414] text-white/45 active:scale-95"><Icon size={18} /><span className="font-mono text-[7px] font-black uppercase">Add</span></button>
      )}
    </div>
  );
}

function GameDemoFlow({ onBack }) {
  const [name, setName] = useState("");
  const [strafeLeft, setStrafeLeft] = useState(false);
  const [strafeRight, setStrafeRight] = useState(false);
  const [spawnRule, setSpawnRule] = useState("STANDARD");
  const [speedMode, setSpeedMode] = useState(1.0);   // 0.5 slow, 1.0 normal, 2.0 fast
  const [facingCamera, setFacingCamera] = useState(false);
  const [gameType, setGameType] = useState("SHOOTER"); // SHOOTER | RACING | WALKAROUND
  const [missionBg, setMissionBg] = useState(null);    // null = neon grid, else backdrop template URL
  const [trafficDensity, setTrafficDensity] = useState("moderate");
  const [boostPads, setBoostPads] = useState("on");
  const [rivalType, setRivalType] = useState("sports");
  const [walkPacing, setWalkPacing] = useState("daylight");
  const [walkLayout, setWalkLayout] = useState("wood");
  const [dangerAlert, setDangerAlert] = useState("calm");
  const [hero, setHero] = useState(null);
  const [troop, setTroop] = useState(null);
  const [villain, setVillain] = useState(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [useSnap, setUseSnap] = useState(false);

  useEffect(() => () => { [hero, troop, villain].forEach((x) => x?.src && URL.revokeObjectURL(x.src)); }, []); // eslint-disable-line

  const slug = (name.trim() || "game_demo").replace(/[^a-z0-9]+/gi, "_").toLowerCase();

  const start = () => {
    if (!name.trim()) { toast.error("Name your demo first"); return; }
    setResult(null); setRecording(true);
  };
  const onErr = (e) => { setRecording(false); console.error(e); toast.error("Recording isn't supported on this browser"); };

  // Project workspace: splash → New/Continue → name + stage tracking.
  const [projectStarted, setProjectStarted] = useState(false);
  const [stage, setStage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [showContinue, setShowContinue] = useState(false);
  const [newName, setNewName] = useState("");
  const [projects, setProjects] = useState(null);
  const [listBusy, setListBusy] = useState(false);
  const [loadingName, setLoadingName] = useState("");

  const SPEED_LABEL = { 0.5: "SLOW", 1.0: "NORMAL", 2.0: "FAST" };
  const buildConfig = () => ({
    active_game_type: gameType,
    speed_setting: SPEED_LABEL[speedMode] || "NORMAL",
    camera_view: facingCamera ? "WALKING_TOWARD" : "RUNNING_AWAY",
    spawning_rule: spawnRule,
    extras: { strafeLeft, strafeRight, trafficDensity, boostPads, rivalType, walkPacing, walkLayout, dangerAlert, missionBg },
  });
  const collectSprites = () => {
    const sprites = {}; const keepSprites = {};
    [["hero", hero], ["troop", troop], ["villain", villain]].forEach(([k, v]) => {
      if (v?.file) sprites[k] = v.file; else if (v?.assetId) keepSprites[k] = v.assetId;
    });
    return { sprites, keepSprites };
  };
  const persistProject = async ({ stageVal, stageAssetId, snapshotDataUrl } = {}) => {
    const pname = name.trim(); if (!pname) return;
    const { sprites, keepSprites } = collectSprites();
    try {
      const res = await pipelinesApi.gameProjectSave({ name: pname, config: buildConfig(), stage: stageVal ?? stage, sprites, keepSprites, snapshotDataUrl, stageAssetId });
      const slots = res?.sprite_slots || {};
      // Absorb persisted asset ids so we don't re-upload the same sprite next save.
      setHero((x) => (x?.file && slots.hero ? { src: x.src, assetId: slots.hero } : x));
      setTroop((x) => (x?.file && slots.troop ? { src: x.src, assetId: slots.troop } : x));
      setVillain((x) => (x?.file && slots.villain ? { src: x.src, assetId: slots.villain } : x));
    } catch (e) { /* non-blocking persistence */ }
  };

  const startProject = async () => {
    const n = newName.trim(); if (!n) { toast.error("Name your project"); return; }
    setName(n); setStage(1); setProjectStarted(true); setShowNew(false);
    try { await pipelinesApi.gameProjectSave({ name: n, config: buildConfig(), stage: 1 }); } catch { /* noop */ }
  };
  const saveAndNext = async () => {
    if (snapshot) setUseSnap(true);
    const cur = stage; const next = cur + 1;
    setStage(next); setResult(null);
    await persistProject({ stageVal: next });
    toast.success(`Stage ${cur} saved · primed for Stage ${cur + 1}`);
  };

  const openContinue = async () => {
    setShowContinue(true); setListBusy(true);
    try { const r = await pipelinesApi.gameProjectList(); setProjects(r.projects || []); }
    catch (e) { toast.error("Could not load your projects"); setProjects([]); }
    finally { setListBusy(false); }
  };
  const loadProject = async (pname) => {
    setLoadingName(pname);
    try {
      const d = await pipelinesApi.gameProjectLoad(pname);
      const cfg = d.config_settings || {}; const ex = cfg.extras || {};
      setName(d.name); setStage(d.current_stage || 1);
      setGameType(cfg.active_game_type || "SHOOTER");
      setSpeedMode({ SLOW: 0.5, NORMAL: 1.0, FAST: 2.0 }[cfg.speed_setting] ?? 1.0);
      setFacingCamera(cfg.camera_view === "WALKING_TOWARD");
      setSpawnRule(cfg.spawning_rule || "STANDARD");
      setStrafeLeft(!!ex.strafeLeft); setStrafeRight(!!ex.strafeRight);
      if (ex.trafficDensity) setTrafficDensity(ex.trafficDensity);
      if (ex.boostPads) setBoostPads(ex.boostPads);
      if (ex.rivalType) setRivalType(ex.rivalType);
      if (ex.walkPacing) setWalkPacing(ex.walkPacing);
      if (ex.walkLayout) setWalkLayout(ex.walkLayout);
      if (ex.dangerAlert) setDangerAlert(ex.dangerAlert);
      setMissionBg(ex.missionBg || null);
      const slots = d.sprite_slots || {};
      const load = async (aid) => { try { return aid ? await streamAsset(aid) : null; } catch { return null; } };
      const [hSrc, tSrc, vSrc] = await Promise.all([load(slots.hero), load(slots.troop), load(slots.villain)]);
      setHero(slots.hero ? { src: hSrc, assetId: slots.hero } : null);
      setTroop(slots.troop ? { src: tSrc, assetId: slots.troop } : null);
      setVillain(slots.villain ? { src: vSrc, assetId: slots.villain } : null);
      if (d.latest_snapshot_asset_id) { const s = await load(d.latest_snapshot_asset_id); if (s) setSnapshot(s); }
      setResult(null); setProjectStarted(true); setShowContinue(false);
      toast.success(`Loaded "${d.name}" · resume at Stage ${d.current_stage || 1}`);
    } catch (e) { toast.error("Could not load that project"); }
    finally { setLoadingName(""); }
  };
  const deleteProject = async (pname) => {
    try { await pipelinesApi.gameProjectDelete(pname); setProjects((p) => (p || []).filter((x) => x.name !== pname)); toast.success("Project deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const onDone = async (blob, snap) => {
    setRecording(false); setUploading(true);
    try {
      const r = await pipelinesApi.gameDemoSave(blob, name.trim() || "Game Demo", name.trim(), stage);
      setResult({ asset: { id: r.asset_id, track: "video" }, engine: "local_canvas" });
      if (snap) setSnapshot(snap);
      await persistProject({ stageAssetId: r.asset_id, snapshotDataUrl: snap });
      toast.success(`Stage ${stage} recorded → filed in "${name.trim()}" album ✓`);
    } catch (e) { toast.error(pipelineErr(e, "Could not save the recording")); }
    finally { setUploading(false); }
  };

  if (!projectStarted) {
    return (
      <div className="h-full w-full flex flex-col overflow-hidden" data-testid="gamedemo-splash">
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-3 pb-6 space-y-3">
          {onBack && (
            <button onClick={onBack} data-testid="gamedemo-splash-back" className="flex items-center gap-1.5 border-2 border-black bg-[#141414] text-white px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000]"><ChevronLeft size={13} /> Director Hub</button>
          )}
          <div className="border-2 border-black bg-[#0d0d0d] shadow-[4px_4px_0px_#000] p-5 text-center space-y-4">
            <p className="font-serif text-xl text-white">🎬 Game Demo Workspace</p>
            {!showNew && !showContinue ? (
              <>
                <button onClick={() => { setNewName(""); setShowNew(true); }} data-testid="gamedemo-new-project" className="w-full border-2 border-black bg-[#00F5D4] text-black py-4 font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000]">🚀 Start New Project</button>
                <button onClick={openContinue} data-testid="gamedemo-continue-project" className="w-full border-2 border-black bg-[#141414] text-white/70 py-4 font-mono text-[11px] font-black uppercase tracking-widest">📁 Continue Existing Project</button>
              </>
            ) : showNew ? (
              <div className="space-y-3" data-testid="gamedemo-new-modal">
                <input value={newName} onChange={(e) => setNewName(e.target.value.slice(0, 60))} autoFocus data-testid="gamedemo-new-name" placeholder="Project name…" className="w-full bg-[#141414] text-white placeholder:text-white/40 border-2 border-black px-3 py-3 font-mono text-sm outline-none focus:ring-2 focus:ring-[#00F5D4]" />
                <button onClick={startProject} data-testid="gamedemo-new-create" className="w-full border-2 border-black bg-[#00F5D4] text-black py-3 font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000]">Create Project</button>
                <button onClick={() => setShowNew(false)} className="w-full font-mono text-[9px] uppercase tracking-widest text-white/40">Cancel</button>
              </div>
            ) : (
              <div className="space-y-2 text-left" data-testid="gamedemo-continue-modal">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] font-black uppercase tracking-widest text-white/70">Your Projects</p>
                  <button onClick={() => setShowContinue(false)} data-testid="gamedemo-continue-close" className="font-mono text-[9px] uppercase tracking-widest text-white/40">Close</button>
                </div>
                {listBusy && <div className="flex items-center gap-2 text-white/60 font-mono text-[10px] py-3"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
                {!listBusy && projects && projects.length === 0 && (
                  <p className="font-mono text-[9px] text-white/40 py-6 text-center" data-testid="gamedemo-projects-empty">No saved projects yet. Start a new one!</p>
                )}
                <div className="space-y-1.5 max-h-[46vh] overflow-y-auto no-scrollbar">
                  {(projects || []).map((p) => {
                    const tid = p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
                    return (
                      <div key={p.name} data-testid={`gamedemo-project-${tid}`} className="flex items-center gap-2 border-2 border-black bg-[#141414] p-2">
                        <button onClick={() => loadProject(p.name)} disabled={!!loadingName} className="flex-1 text-left min-w-0">
                          <p className="font-mono text-[10px] font-black text-white truncate">{p.name}</p>
                          <p className="font-mono text-[8px] text-white/45">{p.game_type} · Stage {p.current_stage} · {p.stages} clip{p.stages === 1 ? "" : "s"}</p>
                        </button>
                        {loadingName === p.name ? (
                          <Loader2 size={14} className="animate-spin text-[#00F5D4]" />
                        ) : (
                          <>
                            <button onClick={() => loadProject(p.name)} data-testid={`gamedemo-open-${tid}`} className="border-2 border-black bg-[#00F5D4] text-black px-2 py-1.5 font-mono text-[8px] font-black uppercase tracking-wide">Open</button>
                            <button onClick={() => deleteProject(p.name)} data-testid={`gamedemo-delete-${tid}`} className="border-2 border-black bg-[#141414] text-red-400 w-7 h-7 grid place-items-center shrink-0"><Trash2 size={12} /></button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="gamedemo-flow">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-3 space-y-4">
        {onBack && (
          <button onClick={onBack} data-testid="gamedemo-back"
            className="flex items-center gap-1.5 border-2 border-black bg-[#141414] text-white px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
            <ChevronLeft size={13} /> Director Hub
          </button>
        )}
        <p className="font-mono text-[10px] text-white/60 leading-relaxed border-2 border-dashed border-black/30 p-2.5">
          Records the real in-app game engine to a <span className="text-gold">6-second</span> MP4 — no cloud render, no wait. Add your Hero / Trooper / Villain art, pick a movement, and hit record. Saves to your <span className="text-gold">Game 6s</span> vault.
        </p>

        {/* 1 · Name */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1.5">Demo name</p>
          <input value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} data-testid="gamedemo-name"
            placeholder="e.g. neon_road_alpha"
            className="w-full bg-[#141414] text-white placeholder:text-white/40 border-2 border-black px-3 py-2.5 font-mono text-[11px] outline-none focus:ring-2 focus:ring-gold" />
        </div>

        {/* 2 · Sprites */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1.5">Sprites (optional)</p>
          <div className="grid grid-cols-3 gap-2" data-testid="gamedemo-sprites">
            <GDSlot label="Hero" val={hero} onSet={setHero} Icon={User} tid="gamedemo-slot-hero" />
            <GDSlot label="Trooper" val={troop} onSet={setTroop} Icon={ShieldAlert} tid="gamedemo-slot-troop" />
            <GDSlot label="Villain" val={villain} onSet={setVillain} Icon={Mountain} tid="gamedemo-slot-villain" />
          </div>
          <p className="font-mono text-[7px] text-white/40 mt-1">Leave empty to use the built-in neon vector units.</p>
        </div>

        {/* Game type selector */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1.5">Game type</p>
          <div className="grid grid-cols-3 gap-2" data-testid="gamedemo-types">
            {[
              { k: "SHOOTER", emoji: "🧟", title: "Last Z Survival", sub: "Shooter Runner" },
              { k: "RACING", emoji: "🏎️", title: "Formula 1", sub: "High-Speed Chase" },
              { k: "WALKAROUND", emoji: "🚶", title: "Last Asylum", sub: "Survival Walk" },
            ].map((g) => (
              <button key={g.k} onClick={() => setGameType(g.k)} data-testid={`gamedemo-type-${g.k.toLowerCase()}`}
                className={`p-2.5 border-2 border-black text-center shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${gameType === g.k ? "bg-[#00F5D4] text-black" : "bg-[#141414] text-white/55"}`}>
                <div className="text-[18px] leading-none mb-1">{g.emoji}</div>
                <div className="font-mono text-[9px] font-black uppercase leading-tight">{g.title}</div>
                <div className="font-mono text-[7px] opacity-70">{g.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Mission backdrop template — sets the gameplay angle; gates/barrels overlay on top */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1.5">Mission backdrop <span className="text-white/40 normal-case tracking-normal">· sets the road angle, overlays sit on top</span></p>
          <div className="grid grid-cols-3 gap-2" data-testid="gamedemo-backdrops">
            {MISSION_BGS.map((b) => (
              <button key={b.k} onClick={() => setMissionBg(b.url)} data-testid={`gamedemo-bg-${b.k}`}
                className={`border-2 border-black overflow-hidden text-left shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${missionBg === b.url ? "ring-2 ring-[#00F5D4]" : ""}`}>
                <div className="aspect-[3/4] bg-[#141414] grid place-items-center overflow-hidden">
                  {b.url ? <img src={b.url} alt={b.label} className="w-full h-full object-cover" /> : <span className="font-mono text-[8px] text-white/40">NEON</span>}
                </div>
                <div className={`px-1 py-1 ${missionBg === b.url ? "bg-[#00F5D4] text-black" : "bg-[#141414] text-white/60"}`}>
                  <div className="font-mono text-[8px] font-black uppercase leading-tight truncate">{b.label}</div>
                  <div className="font-mono text-[7px] opacity-70 truncate">{b.sub}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {gameType === "SHOOTER" && (<>
        {/* 3 · Movement macro — forward always on; strafe L/R multi-select */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1.5">Movement macro <span className="text-white/40 normal-case tracking-normal">· forward always on · speed 65</span></p>
          <div className="grid grid-cols-3 gap-1.5" data-testid="gamedemo-macros">
            <div data-testid="gamedemo-macro-forward" style={{ backgroundColor: "#00F5D4" }}
              className="flex flex-col items-center gap-1.5 border-2 border-black px-2 py-3 font-mono text-[9px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] text-black">
              <ArrowUp size={18} /> Forward
            </div>
            <button onClick={() => setStrafeLeft((v) => !v)} data-testid="gamedemo-macro-strafe_l"
              style={strafeLeft ? { backgroundColor: "#A4DCFA" } : {}}
              className={`flex flex-col items-center gap-1.5 border-2 border-black px-2 py-3 font-mono text-[9px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${strafeLeft ? "text-black" : "bg-[#141414] text-white/60"}`}>
              <ArrowLeft size={18} /> Strafe Left
            </button>
            <button onClick={() => setStrafeRight((v) => !v)} data-testid="gamedemo-macro-strafe_r"
              style={strafeRight ? { backgroundColor: "#c084fc" } : {}}
              className={`flex flex-col items-center gap-1.5 border-2 border-black px-2 py-3 font-mono text-[9px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${strafeRight ? "text-black" : "bg-[#141414] text-white/60"}`}>
              <ArrowRight size={18} /> Strafe Right
            </button>
          </div>
          {strafeLeft && strafeRight && (
            <p className="mt-1 font-mono text-[8px] text-white/45" data-testid="gamedemo-strafe-cycle">Cycle: 0.8s left → 3.0s right, looping</p>
          )}
        </div>

        {/* 4 · Spawning rule */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1.5">Spawning rule <span className="text-white/40 normal-case tracking-normal">· what the 6s loop generates</span></p>
          <div className="grid grid-cols-2 gap-1.5" data-testid="gamedemo-spawn-rules">
            {SPAWN_RULES.map((s) => (
              <button key={s.k} onClick={() => setSpawnRule(s.k)} data-testid={`gamedemo-spawn-${s.k.toLowerCase()}`}
                style={spawnRule === s.k ? { backgroundColor: s.accent } : {}}
                className={`flex items-center gap-2 border-2 border-black px-2.5 py-2.5 font-mono text-[9px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${spawnRule === s.k ? "text-black" : "bg-[#141414] text-white/60"}`}>
                <span className="text-[13px] leading-none">{s.emoji}</span> {s.label}
              </button>
            ))}
          </div>
        </div>

        </>)}

        {gameType === "RACING" && (
          <div className="space-y-3" data-testid="gamedemo-panel-racing">
            <GDSeg label="Traffic density" value={trafficDensity} onChange={setTrafficDensity} tid="racing-traffic"
              options={[{ v: "low", label: "Low" }, { v: "moderate", label: "Moderate" }, { v: "extreme", label: "Gridlock" }]} />
            <GDSeg label="Boost pads" value={boostPads} onChange={setBoostPads} tid="racing-boost"
              options={[{ v: "on", label: "On" }, { v: "off", label: "Off" }]} />
            <GDSeg label="Rival cars" value={rivalType} onChange={setRivalType} tid="racing-rivals"
              options={[{ v: "sports", label: "Sports" }, { v: "police", label: "Police" }, { v: "trucks", label: "Trucks" }]} />
            <p className="font-mono text-[8px] text-white/40">Formula 1 racer engine is live — records a high-speed lane-weaving run with rival traffic and boost pads. The car sprite uses your Hero upload.</p>
          </div>
        )}

        {gameType === "WALKAROUND" && (
          <div className="space-y-3" data-testid="gamedemo-panel-walkaround">
            <GDSeg label="Environmental pacing" value={walkPacing} onChange={setWalkPacing} tid="walk-pacing"
              options={[{ v: "daylight", label: "Daylight" }, { v: "night", label: "Night Blizzard" }]} />
            <GDSeg label="Gathering layout" value={walkLayout} onChange={setWalkLayout} tid="walk-layout"
              options={[{ v: "wood", label: "Wood" }, { v: "ice", label: "Ice Mining" }, { v: "shelter", label: "Shelter" }]} />
            <GDSeg label="Danger alert" value={dangerAlert} onChange={setDangerAlert} tid="walk-danger"
              options={[{ v: "calm", label: "Calm Base" }, { v: "threat", label: "Zombie Threat" }]} />
            <p className="font-mono text-[8px] text-white/40">Last Asylum survival engine is live — records an atmospheric flashlight-lit walk through a dark grid: collect keys, unlock doors, recover glowing canisters. Uses your Hero upload as the survivor.</p>
          </div>
        )}

        {/* 5 · Speed */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1.5">Speed</p>
          <div className="grid grid-cols-3 gap-1.5" data-testid="gamedemo-speeds">
            {[{ v: 0.5, label: "Slow-Mo" }, { v: 1.0, label: "Normal" }, { v: 2.0, label: "Fast-Forward" }].map((s) => (
              <button key={s.v} onClick={() => setSpeedMode(s.v)} data-testid={`gamedemo-speed-${s.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={`border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${speedMode === s.v ? "bg-[#00F5D4] text-black" : "bg-[#141414] text-white/60"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* 6 · Camera view */}
        <div>
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1.5">Camera view</p>
          <div className="grid grid-cols-2 gap-1.5" data-testid="gamedemo-camera">
            <button onClick={() => setFacingCamera(false)} data-testid="gamedemo-camera-away"
              className={`flex items-center justify-center gap-1.5 border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] ${!facingCamera ? "bg-gold text-black" : "bg-[#141414] text-white/60"}`}>
              <ArrowUp size={15} /> Running Away
            </button>
            <button onClick={() => setFacingCamera(true)} data-testid="gamedemo-camera-toward"
              className={`flex items-center justify-center gap-1.5 border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] ${facingCamera ? "bg-gold text-black" : "bg-[#141414] text-white/60"}`}>
              <ArrowDown size={15} /> Walking Toward
            </button>
          </div>
        </div>

        {/* Snapshot chaining */}
        {snapshot && (
          <button onClick={() => setUseSnap((v) => !v)} data-testid="gamedemo-use-snapshot"
            className={`w-full flex items-center gap-2 border-2 border-black px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000] ${useSnap ? "bg-gold text-black" : "bg-[#141414] text-white/70"}`}>
            <img src={snapshot} alt="last frame" className="w-8 h-8 object-cover border border-white/30" />
            {useSnap ? "Next demo continues from last frame ✓" : "Continue next demo from last frame"}
          </button>
        )}

        {uploading && <div className="flex items-center gap-2 font-mono text-[10px] text-white/60" data-testid="gamedemo-uploading"><Loader2 size={14} className="animate-spin" /> Encoding & saving…</div>}

        {result?.asset?.id && (
          <>
            <ResultCard result={result} />
            <div className="pt-1" data-testid="gamedemo-save">
              <p className="font-mono text-[9px] font-black uppercase tracking-[0.14em] text-white/50 mb-1.5">Save · {name} · Stage {stage}</p>
              <SaveDestinations assetId={result.asset.id} track="VIDEOS" album={name} filename={`${slug}_stage${stage}_6s`} ext="mp4" commit />
              <div className="mt-1 flex items-center gap-2 border-2 border-black bg-black/40 px-2 py-1.5">
                <span className="font-mono text-[8px] font-black uppercase tracking-widest text-[#00F5D4]">✓ Filed in "{name}" project album</span>
              </div>
              <button onClick={saveAndNext} data-testid="gamedemo-save-next"
                className="mt-2 w-full flex items-center justify-center gap-2 border-2 border-black bg-gold text-black py-3 font-mono text-[10px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                <ArrowDown size={14} /> Save &amp; Next Scene (Stage {stage + 1})
              </button>
            </div>
          </>
        )}
      </div>
      <div className="shrink-0 border-t-4 border-[#2a2a2a] bg-[#141414] px-4 py-3" data-testid="gamedemo-bar">
        <button onClick={start} disabled={recording || uploading || !name.trim()} data-testid="gamedemo-run"
          className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-black rounded-full shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Gamepad2 size={16} />} {uploading ? "Saving…" : "Record Game Demo (6s)"}
        </button>
      </div>

      {recording && (
        <GameDemoRecorder durationMs={6000} speed={Math.round(65 * speedMode)} spawnRule={spawnRule} facingCamera={facingCamera} onDone={onDone} onError={onErr}
          gameType={gameType}
          racing={{ trafficDensity, boostPads, rivalType }}
          walk={{ pacing: walkPacing, layout: walkLayout, danger: dangerAlert }}
          strafe={{ left: strafeLeft, right: strafeRight, leftDur: 0.8, rightDur: 3.0 }}
          sprites={{ hero: hero?.src, troop: troop?.src, villain: villain?.src }}
          backdrop={useSnap && snapshot ? snapshot : missionBg}
          bgFull={!!missionBg && !(useSnap && snapshot)}
          barrelStyle={missionBg === "/assets/game/corridor_shooter_bg.png" ? "steel" : "wood"} />
      )}
    </div>
  );
}

/* ============================ DIRECTOR HUB ============================== */
// Unifies the two creation experiences under one entry point: an AI Agent mode
// (the storyboard→video BYOK DirectorFlow) and a Manual Builder (ProjectVault).
function UnifiedDirectorHub() {
  const [subView, setSubView] = useState("hub");

  if (subView === "vault") return <ProjectVaultFlow onBack={() => setSubView("hub")} />;
  if (subView === "gamedemo") return <GameDemoFlow onBack={() => setSubView("hub")} />;

  const CARDS = [
    {
      key: "vault", title: "Manual Builder", Icon: Clapperboard, color: "#A4DCFA",
      desc: "Build your video scene-by-scene: character + scene references, movement macros, and a working-prompt album — styled like Mist Create.",
    },
    {
      key: "gamedemo", title: "Manual Game Demo", Icon: Gamepad2, color: "#00F5D4",
      desc: "One image + a gameplay macro (Road Shooter, Space Battle, Battle Arena) → an instant 6–10s demo MP4 saved to your Game 6s library.",
    },
  ];

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="director-hub">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-3">
        <p className="font-mono text-[10px] text-white/60 leading-relaxed border-2 border-dashed border-black/30 p-2.5">
          Choose how you want to direct your video.
        </p>
        {CARDS.map((c) => (
          <button key={c.key} onClick={() => setSubView(c.key)} data-testid={`director-hub-${c.key}`}
            className="w-full flex items-start gap-3 border-2 border-black p-4 text-left shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform"
            style={{ backgroundColor: c.color }}>
            <span className="w-14 h-14 border-2 border-black bg-[#141414] grid place-items-center shadow-[2px_2px_0px_#000] shrink-0">
              <c.Icon size={26} strokeWidth={2.3} style={{ color: c.color }} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-serif text-2xl leading-none text-black">{c.title}</p>
              <p className="font-mono text-[9px] leading-relaxed text-black/70 mt-2">{c.desc}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ================================ ROUTER ================================= */
export default function MistCreate() {
  const [view, setView] = useStudioParam("view", ""); // "" landing | generate | animate | effects

  if (view) {
    return (
      <div className="h-full w-full flex flex-col overflow-hidden" data-testid={`mist-create-${view}`}>
        <div className="shrink-0 flex items-center gap-2 px-4 pt-3 pb-2 border-b-2 border-black bg-[#141414]">
          <button onClick={() => setView("")} data-testid="mist-create-back"
            className="w-8 h-8 grid place-items-center border-2 border-black bg-[#141414] rounded-none shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><ChevronLeft size={16} /></button>
          <h3 className="font-serif text-xl text-white capitalize">{{ strip: "Overlay Removal", director: "Director Mode" }[view] || view}</h3>
          <div className="flex-1" />
          <CoinBalanceChip />
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          {view === "generate" && <GenerateFlow />}
          {view === "director" && <UnifiedDirectorHub />}
          {view === "animate" && <AnimateFlow />}
          {view === "effects" && <EffectsWorkspace />}
          {view === "strip" && <OverlayRemovalFlow />}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="mist-create-landing">
      <div className="shrink-0 flex items-center justify-between px-4 pt-3 pb-2 border-b-2 border-black bg-[#141414]">
        <h3 className="font-serif text-xl text-white">Mist Create</h3>
        <CoinBalanceChip />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-3">
        <p className="font-mono text-[10px] text-white/60 leading-relaxed border-2 border-dashed border-black/30 p-2.5">
          Pick a creation path.
        </p>
        {OPTIONS.map((o) => (
          <button key={o.key} onClick={() => setView(o.key)} data-testid={`mist-create-opt-${o.key}`}
            className="w-full flex items-center gap-3 border-2 border-black p-4 text-left shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform"
            style={{ backgroundColor: o.color }}>
            <span className="w-14 h-14 border-2 border-black bg-[#141414] grid place-items-center shadow-[2px_2px_0px_#000] shrink-0">
              <o.Icon size={26} strokeWidth={2.3} style={{ color: o.color }} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-serif text-2xl leading-none text-black">{o.title}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-black/60 mt-1.5">{o.sub}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
